<template lang="pug">
//- The landing page. The copy on this page is also written out as static HTML
//- for crawlers and link previews (homeBodyHtml in build/seo-plugins.ts). When
//- the copy here changes, change it there too, and keep docs/TRACKING.md in
//- step with the data-track names.
//-
//- The order: the hero; what you can build and what Skapi provides for it;
//- building with AI agents (the flow, fewer tokens, any platform); performance
//- and price; DDBD, the database underneath; frameworks with npm and
//- GitHub; how it works (codebase, the agent's three steps, the MCP server);
//- regions, plans, news, and the invitation with the one button last. The
//- headline and the sub line are constants in the script (HEADLINE,
//- HEADLINE_SUB). Blocks marked v-reveal fade in as they scroll into view, and
//- their looping drawings run only while on screen (vReveal in the script).
.bq-landing
    main
        //- ─── hero: the pitch on the left, the backend drawn on the right ───
        section#what.bq-hero
            .bq-wrap.bq-hero-grid
                .bq-hero-copy
                    //- Text art in place of a logo image: it idles through its
                    //- motions. The art and every motion live in the component.
                    AsciiBanner(:reveal="false")

                    //- The page's one h1, visible this time: it is the headline.
                    h1.bq-hero-title
                        template(v-for="(part, i) in HEADLINE" :key="part")
                            | {{ i ? ' ' : '' }}
                            span.wordset {{ part }}
                    p.bq-hero-sub {{ HEADLINE_SUB }}

                    .bq-hero-actions
                        router-link.bq-btn(
                            data-track="landing_hero_try",
                            :to="user?.user_id ? '/my-projects' : '/signup'"
                        ) Try Skapi &rarr;
                        a.bq-cta-link(href="https://docs.skapi.com" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="hero")
                            | Read the documentation
                            span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

                    //- //- The opening prompt the docs and the Settings page teach,
                    //- //- in place of the install line: it is what a visitor with
                    //- //- an agent does first. Two lines: a br, since Vue collapses
                    //- //- a newline in text to a space.
                    //- .bq-code-option.bq-hero-install
                    //-     span.bq-code-caption prompt
                    //-     CopyText.bq-code(:value="promptText")
                    //-         code
                    //-             | My Skapi project ID is: #[span.tok-str "&lt;Project ID&gt;"].
                    //-             br
                    //-             | Build me a #[span.tok-p [describe what you want]].
                    //-         span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy

                //- A stack of four server units on a grid floor, each unit
                //- wired to two of the eight services, and above it the agent's
                //- terminal, where short prompts are typed one after another and
                //- land on the stack. Decorative: the tile words are the feature
                //- grid's, which the page has below, and the prompts are demo
                //- filler (PROMPTS in the script), not site copy.
                .bq-hero-art(aria-hidden="true")
                    //- On a phone the drawing is about half size, so the terminal
                    //- is scaled up two times around its bottom edge and the
                    //- viewBox gains the 80 units of headroom it grows into.
                    svg.bq-art(:viewBox="phone ? '0 -80 640 550' : '0 0 640 470'" fill="none")
                        defs
                            radialGradient#bq-floor-grad(cx="320" cy="300" r="300" gradientUnits="userSpaceOnUse")
                                stop(offset="0.25" stop-color="#fff")
                                stop(offset="1" stop-color="#fff" stop-opacity="0")
                            mask#bq-floor-fade(maskUnits="userSpaceOnUse" x="0" y="0" width="640" height="470")
                                rect(x="0" y="0" width="640" height="470" fill="url(#bq-floor-grad)")
                        //- the floor
                        g.bq-art-floor(mask="url(#bq-floor-fade)")
                            line(v-for="(l, i) in FLOOR" :key="i" :x1="l[0]" :y1="l[1]" :x2="l[2]" :y2="l[3]")
                        //- the wires, each with a second stroke of moving dashes
                        g(v-for="w in WIRES" :key="w.d")
                            path.bq-art-wire(:d="w.d")
                            path.bq-art-flow(:d="w.d" :class="{ 'bq-art-flow--out': w.out }")
                        //- the stack, bottom unit first so each one covers the one below
                        g.bq-art-slab(v-for="(s, i) in SLABS" :key="i" :class="{ 'bq-art-slab--top': s.isTop }")
                            polygon.bq-art-face-top(:points="s.top")
                            polygon.bq-art-face-left(:points="s.left")
                            polygon.bq-art-face-right(:points="s.right")
                            polygon.bq-art-led(v-for="(p, j) in s.leds" :key="j" :points="p")
                            line.bq-art-vent(v-for="(v, j) in s.vents" :key="'v' + j" :x1="v[0]" :y1="v[1]" :x2="v[2]" :y2="v[3]")
                            rect.bq-art-port(:x="s.portL[0] - 3" :y="s.portL[1] - 3" width="6" height="6")
                            rect.bq-art-port(:x="s.portR[0] - 3" :y="s.portR[1] - 3" width="6" height="6")
                        //- the landing pad on the top unit, and the agent's
                        //- terminal above it: the prompt comes down the beam
                        polygon.bq-art-pad(:points="PAD")
                        path.bq-art-beam(d="M320 68V124")
                        path.bq-art-flow(d="M320 68V124")
                        //- placed by the outer group; the middle one is scaled
                        //- up on a phone; the inner one bobs (a CSS transform
                        //- would replace the placement attribute, so each has its own)
                        g(transform="translate(225 6)")
                          g.bq-art-term-scale
                            g.bq-art-term.bq-art-term--bob
                                rect.bq-art-term-box(x="0" y="0" width="190" height="62" rx="3")
                                line.bq-art-term-bar(x1="0" y1="16" x2="190" y2="16")
                                circle.bq-art-term-dot(v-for="x in [10, 20, 30]" :key="x" :cx="x" cy="8" r="2.2")
                                text.bq-art-term-text(x="10" y="36") &gt; {{ typed[0] }}#[tspan.bq-art-cursor(v-if="cursorLine === 0" translate="no") &#9615;]
                                text.bq-art-term-text(x="10" y="52" v-if="cursorLine === 1") &gt; {{ typed[1] }}#[tspan.bq-art-cursor(v-if="cursorLine === 1" translate="no") &#9615;]
                        //- the service tiles
                        g.bq-art-tile(v-for="t in TILES" :key="t.label[0]")
                            rect.bq-art-tile-shadow(:x="t.x - 25" :y="t.y - 25" width="56" height="56")
                            rect.bq-art-tile-box(:x="t.x - 28" :y="t.y - 28" width="56" height="56")
                            Glyph(as="g" :shapes="t.icon" :transform="`translate(${t.x - 12} ${t.y - 12})`")
                            text.bq-art-label(:x="t.x" :y="t.y + 47")
                                tspan(v-for="(line, i) in t.label" :key="line" :x="t.x" :dy="i ? 16 : 0") {{ line }}

        //- ─── what is skapi: what you can build, then what ships for it ───
        //- The drawing is an app window with each kind of work pointed out. The
        //- callouts are links to the numbered rows under it; on a phone the view
        //- crops to the window and only the numbered markers stay.
        section#features.bq-section
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill WHAT IS SKAPI
                        h2 Build Your App. #[span.wordset Skapi Is the Backend.]
                    p.bq-section-sub Skapi is a serverless backend you use through one JavaScript library. Here is what you can build with it, and what ships with every project for it.
                .bq-app-art(v-reveal aria-hidden="true")
                    svg.bq-art(:viewBox="phone ? '222 8 556 520' : '0 0 1000 540'" fill="none")
                        //- the window and its address bar
                        rect.ill-surface(x="230" y="20" width="540" height="500" rx="4")
                        line.ill-ink(x1="230" y1="52" x2="770" y2="52")
                        circle.bq-art-term-dot(v-for="x in [246, 258, 270]" :key="x" :cx="x" cy="36" r="3")
                        rect.app-url(x="296" y="27" width="304" height="18" rx="9")
                        path.ill-accent.app-thin(d="M310 34v-2a3 3 0 0 1 6 0v2M308.5 34h9v6h-9z")
                        text.app-text(x="324" y="40") https://myapp.skapi.com
                        //- 1: the login card
                        rect.app-card(x="246" y="68" width="138" height="152")
                        circle.app-soft-ring(cx="315" cy="96" r="15")
                        circle.ill-ink(cx="315" cy="92" r="5")
                        path.ill-ink(d="M306 106a9 7 0 0 1 18 0")
                        rect.app-field(x="258" y="120" width="114" height="16")
                        text.app-text(x="264" y="132") you@mail.com
                        rect.app-field(x="258" y="142" width="114" height="16")
                        circle.app-dot(v-for="k in 6" :key="k" :cx="258 + k * 8" cy="150" r="2")
                        rect.app-btn(x="258" y="164" width="114" height="18")
                        text.app-btn-text(x="315" y="176.5") Sign in
                        rect.app-field(x="258" y="190" width="114" height="18")
                        text.app-text.app-mid(x="315" y="202.5") OpenID login
                        //- 5: the newsletter card
                        rect.app-card(x="246" y="234" width="138" height="92")
                        text.app-text.app-text--ink(x="258" y="254") Newsletter
                        rect.app-field(x="258" y="264" width="114" height="16")
                        text.app-text(x="264" y="276") email
                        rect.app-btn(x="258" y="288" width="114" height="18")
                        text.app-btn-text(x="315" y="300.5") Subscribe
                        //- 4: a widget fed by a third-party API
                        rect.app-card(x="246" y="340" width="138" height="72")
                        circle.ill-accent(cx="272" cy="370" r="7")
                        path.ill-accent(d="M272 357v-3M272 386v-3M259 370h-3M288 370h-3")
                        text.app-big(x="294" y="377") 21°C
                        text.app-text(x="258" y="402") Weather API
                        //- 6: a payment
                        rect.app-card(x="246" y="426" width="138" height="80")
                        text.app-text.app-text--ink(x="258" y="446") Pro plan
                        text.app-text(x="258" y="462") $9 / month
                        rect.app-btn(x="258" y="476" width="114" height="18")
                        text.app-btn-text(x="315" y="488.5") Pay
                        //- 2: a post with a photo
                        rect.app-card(x="400" y="68" width="216" height="196")
                        rect.app-soft(x="412" y="80" width="192" height="116")
                        path.ill-ink(d="M412 196L464 146L498 176L528 152L604 196")
                        circle.ill-accent(cx="574" cy="106" r="10")
                        text.app-text.app-text--ink(x="412" y="216") Trip to the coast
                        line.ill-line(x1="412" y1="230" x2="590" y2="230")
                        path.ill-accent.app-thin(d="M418 252l-5.5-5.2a3.2 3.2 0 0 1 5.5-3.3 3.2 3.2 0 0 1 5.5 3.3z")
                        text.app-text(x="430" y="253") 12
                        //- 3: a call and a chat
                        rect.app-card(x="630" y="68" width="126" height="196")
                        rect.app-soft(x="640" y="78" width="106" height="70")
                        circle.ill-ink(cx="693" cy="102" r="10")
                        path.ill-ink(d="M676 148a17 14 0 0 1 34 0")
                        rect.app-card(x="718" y="122" width="22" height="20")
                        circle.app-live(cx="651" cy="89" r="3")
                        g.app-pop(style="--k: 0")
                            rect.app-card(x="640" y="158" width="62" height="18" rx="9")
                            text.app-text(x="652" y="170.5") Hey!
                        g.app-pop(style="--k: 1")
                            rect.app-btn(x="672" y="184" width="74" height="18" rx="9")
                            text.app-btn-text(x="709" y="196.5") On my way
                        g.app-pop(style="--k: 2")
                            rect.app-card(x="640" y="210" width="40" height="18" rx="9")
                            circle.app-dot(v-for="x in [652, 660, 668]" :key="x" :cx="x" cy="219" r="2")
                        rect.app-field(x="640" y="238" width="106" height="16")
                        //- 2: the files and records behind the app
                        rect.app-card(x="400" y="278" width="356" height="228")
                        text.app-text.app-text--ink(x="412" y="298") My files
                        rect.app-btn(x="676" y="286" width="68" height="18")
                        text.app-btn-text(x="710" y="298.5") Upload
                        g(v-for="(f, i) in APP_FILES" :key="f[0]" :transform="`translate(412 ${322 + i * 46})`")
                            line.ill-line(v-if="i" x1="0" y1="-14" x2="332" y2="-14")
                            path.ill-ink.app-thin(d="M0 0h10l4 4v14H0z")
                            text.app-text.app-text--ink(x="26" y="13") {{ f[0] }}
                            text.app-text.app-end(x="332" y="13") {{ f[1] }}
                        //- the callouts: a leader to the part, a marker on it, a chip
                        //- outside the window that links to its row
                        a.bq-callout(
                            v-for="(c, i) in APP_CALLOUTS"
                            :key="c.n"
                            :href="`#can-${c.n}`"
                            :class="`bq-callout--${c.side}`"
                            :style="{ '--i': i }"
                        )
                            path.bq-callout-lead(:d="c.side === 'l' ? `M200 ${c.y}H${c.ax}` : `M800 ${c.y}H${c.ax}`")
                            rect.bq-callout-shadow(:x="c.side === 'l' ? 27 : 803" :y="c.y - 19" width="176" height="44")
                            rect.bq-callout-box(:x="c.side === 'l' ? 24 : 800" :y="c.y - 22" width="176" height="44")
                            Glyph.app-glyph(as="g" :shapes="c.icon" :transform="`translate(${c.side === 'l' ? 38 : 814} ${c.y - 12})`")
                            text.bq-callout-label(:x="c.side === 'l' ? 72 : 848" :y="c.y + 5") {{ c.label }}
                        g.bq-callout-mark(v-for="(c, i) in APP_CALLOUTS" :key="'m' + c.n" :style="{ '--i': i }")
                            circle(:cx="c.ax" :cy="c.y" r="11")
                            text(:x="c.ax" :y="c.y + 4") {{ c.n }}

                //- the seven things, each with its drawing and what Skapi provides for it
                ol.bq-can-list
                    li.bq-can(
                        v-for="(c, i) in ABILITIES"
                        :key="c.n"
                        :id="`can-${c.n}`"
                        v-reveal
                        :class="{ 'bq-can--flip': i % 2 }"
                    )
                        .bq-can-copy
                            span.bq-can-num(translate="no") {{ String(c.n).padStart(2, "0") }}
                            h3 {{ c.title }}
                            p {{ c.desc }}
                            .bq-can-provides
                                span.bq-can-provides-label Skapi provides
                                ul.bq-chips
                                    li.bq-chip(v-for="p in c.provides" :key="p.name")
                                        Glyph.bq-icon(:shapes="p.icon")
                                        span {{ p.name }}
                        .bq-can-art(aria-hidden="true")
                            svg(viewBox="0 0 320 200" fill="none")
                                //- a sign-in card, the password typed, a seal of approval
                                template(v-if="c.art === 'login'")
                                    rect.ill-surface(x="96" y="12" width="128" height="176" rx="3")
                                    circle.app-soft-ring(cx="160" cy="40" r="15")
                                    circle.ill-ink(cx="160" cy="36" r="5")
                                    path.ill-ink(d="M151 50a9 7 0 0 1 18 0")
                                    rect.app-field(x="108" y="66" width="104" height="16")
                                    text.app-text(x="114" y="78") you@mail.com
                                    rect.app-field(x="108" y="88" width="104" height="16")
                                    circle.app-dot.app-type(v-for="k in 6" :key="k" :cx="108 + k * 8" cy="96" r="2" :style="{ '--k': k }")
                                    rect.app-btn(x="108" y="112" width="104" height="18")
                                    text.app-btn-text(x="160" y="124.5") Sign in
                                    line.ill-line(x1="108" y1="144" x2="146" y2="144")
                                    text.app-text.app-mid(x="160" y="147.5") or
                                    line.ill-line(x1="174" y1="144" x2="212" y2="144")
                                    rect.app-field(x="108" y="156" width="104" height="18")
                                    Glyph.app-glyph.app-glyph--sm(as="g" :shapes="ICON.openid" transform="translate(116 159) scale(0.5)")
                                    text.app-text(x="134" y="168.5") OpenID login
                                    g.app-seal
                                        path.app-shield(d="M268 44l22 8v16c0 14-10 22-22 26-12-4-22-12-22-26V52z")
                                        path.ill-accent(d="M259 68l6 6 12-12")
                                //- a file goes up, is kept, and goes out to everyone
                                template(v-else-if="c.art === 'files'")
                                    rect.ill-surface(x="12" y="52" width="84" height="104" rx="3")
                                    line.ill-ink(x1="12" y1="68" x2="96" y2="68")
                                    path.ill-ink(d="M40 82h18l8 8v36H40z")
                                    path.ill-accent(d="M53 118v-18M47 106l6-6 6 6")
                                    path.bq-art-wire(d="M96 104H138")
                                    path.ill-accent.ill-flow(d="M96 104H138")
                                    path.ill-surface(d="M138 68v70c0 5.5 15.2 10 34 10s34-4.5 34-10V68")
                                    path.ill-line(d="M138 92c0 5.5 15.2 10 34 10s34-4.5 34-10M138 116c0 5.5 15.2 10 34 10s34-4.5 34-10")
                                    ellipse.ill-surface.app-soft-fill(cx="172" cy="68" rx="34" ry="10")
                                    path.bq-art-wire(d="M206 104H236")
                                    path.ill-accent.ill-flow(d="M206 104H236")
                                    circle.ill-surface(cx="262" cy="104" r="26")
                                    path.ill-line(d="M236 104h52M262 78c-11 8-11 44 0 52M262 78c11 8 11 44 0 52")
                                    path.ill-accent.ill-flow(d="M281 86L297 58M288 104H303M281 122L297 150")
                                    circle.app-user(v-for="u in [[300, 52], [310, 104], [300, 156]]" :key="u[1]" :cx="u[0]" :cy="u[1]" r="6")
                                    text.app-text.app-mid(x="54" y="182") upload
                                    text.app-text.app-mid(x="172" y="182") store
                                    text.app-text.app-mid(x="262" y="182") share
                                //- two people on a call, chatting, with data going both ways
                                template(v-else-if="c.art === 'realtime'")
                                    g(v-for="wx in [12, 190]" :key="wx")
                                        rect.ill-surface(:x="wx" y="20" width="118" height="160" rx="3")
                                        line.ill-ink(:x1="wx" y1="36" :x2="wx + 118" y2="36")
                                        rect.app-soft(:x="wx + 10" y="46" width="98" height="58")
                                        circle.ill-ink(:cx="wx + 59" cy="68" r="9")
                                        path.ill-ink(:d="`M${wx + 43} 104a16 13 0 0 1 32 0`")
                                        circle.app-live(:cx="wx + 20" cy="56" r="3")
                                        rect.app-card.app-pop(:x="wx + 10" y="116" width="62" height="14" rx="7" :style="{ '--k': wx > 100 ? 1 : 0 }")
                                        rect.app-btn.app-pop(:x="wx + 46" y="138" width="62" height="14" rx="7" :style="{ '--k': wx > 100 ? 3 : 2 }")
                                        rect.app-field(:x="wx + 10" y="160" width="98" height="12")
                                    path.bq-art-wire(d="M130 76H190M190 128H130")
                                    path.ill-accent.ill-flow(d="M130 76H190")
                                    path.ill-accent.ill-flow(d="M190 128H130")
                                    text.app-text.app-mid(x="160" y="68") video
                                    text.app-text.app-mid(x="160" y="120") chat
                                //- your app asks, Skapi adds the key, the API answers
                                template(v-else-if="c.art === 'apikey'")
                                    rect.ill-surface(x="8" y="56" width="88" height="88" rx="3")
                                    line.ill-ink(x1="8" y1="72" x2="96" y2="72")
                                    text.app-text.app-text--ink(x="18" y="96") fetch(
                                    text.app-text.app-text--ink(x="26" y="112") weather)
                                    path.bq-art-wire(d="M96 100H124")
                                    path.ill-accent.ill-flow(d="M96 100H124")
                                    rect.app-vault(x="124" y="44" width="72" height="112" rx="3")
                                    image(href="/skapi-logo-cut-small.png" x="146" y="56" width="28" height="34")
                                    g.app-key
                                        Glyph.app-glyph.app-glyph--accent(as="g" :shapes="ICON.key" transform="translate(148 108)")
                                    path.bq-art-wire(d="M196 100H224")
                                    path.ill-accent.ill-flow(d="M196 100H224")
                                    rect.ill-surface(x="224" y="56" width="88" height="88" rx="3")
                                    line.ill-ink(x1="224" y1="72" x2="312" y2="72")
                                    text.app-text.app-text--ink(x="234" y="96") api.weather
                                    line.ill-line(x1="234" y1="110" x2="300" y2="110")
                                    line.ill-line(x1="234" y1="122" x2="284" y2="122")
                                    text.app-text.app-mid(x="52" y="176") your app
                                    text.app-text.app-mid(x="160" y="176") key added
                                    text.app-text.app-mid(x="268" y="176") 3rd-party API
                                //- one template, out to every inbox
                                template(v-else-if="c.art === 'email'")
                                    rect.ill-surface(x="12" y="36" width="100" height="128" rx="3")
                                    Glyph.app-glyph(as="g" :shapes="ICON.email" transform="translate(50 50)")
                                    text.app-text.app-text--ink.app-mid(x="62" y="96") Welcome!
                                    line.ill-line(x1="26" y1="112" x2="98" y2="112")
                                    line.ill-line(x1="26" y1="124" x2="90" y2="124")
                                    rect.app-btn(x="26" y="138" width="56" height="12")
                                    g(v-for="(y, k) in INBOX_Y" :key="y")
                                        path.bq-art-wire(:d="`M112 100C150 100 160 ${y} 196 ${y}`")
                                        path.ill-accent.ill-flow(:d="`M112 100C150 100 160 ${y} 196 ${y}`")
                                        rect.ill-surface(x="196" :y="y - 14" width="112" height="28" rx="3")
                                        rect.ill-ink.app-thin(x="206" :y="y - 6" width="16" height="12")
                                        path.ill-ink.app-thin(:d="`M206 ${y - 6}l8 6 8-6`")
                                        line.ill-line(x1="230" :y1="y - 4" x2="280" :y2="y - 4")
                                        line.ill-line(x1="230" :y1="y + 5" x2="266" :y2="y + 5")
                                        path.ill-accent.app-pop(:d="`M288 ${y}l4 4 8-8`" :style="{ '--k': k }")
                                //- a payment event comes in, the ticket runs its actions
                                template(v-else-if="c.art === 'tickets'")
                                    rect.ill-surface(x="8" y="72" width="76" height="56" rx="3")
                                    text.app-text.app-text--ink.app-mid(x="46" y="97") Payment
                                    text.app-text.app-mid(x="46" y="113") provider
                                    path.bq-art-wire(d="M84 100H112")
                                    path.ill-accent.ill-flow(d="M84 100H112")
                                    rect.app-vault(x="112" y="80" width="64" height="40" rx="3")
                                    text.app-text.app-accent.app-mid(x="144" y="104") ticket
                                    path.bq-art-wire(d="M176 100H192M192 40V160M192 40H208M192 100H208M192 160H208")
                                    g(v-for="(a, k) in TICKET_ACTIONS" :key="a.label")
                                        rect.ill-surface(x="208" :y="a.y - 16" width="104" height="32" rx="3")
                                        text.app-text.app-text--ink(x="218" :y="a.y + 4") {{ a.label }}
                                        path.ill-accent.app-pop(:d="`M293 ${a.y}l3 3 7-7`" :style="{ '--k': k }")
                                //- your files, up to your own address
                                template(v-else-if="c.art === 'hosting'")
                                    g(v-for="(f, k) in ['index.html', 'style.css', 'app.js']" :key="f")
                                        rect.ill-surface(x="10" :y="52 + k * 34" width="104" height="26" rx="3")
                                        path.ill-ink.app-thin(:d="`M20 ${58 + k * 34}h8l4 4v10H20z`")
                                        text.app-text.app-text--ink(x="38" :y="69 + k * 34") {{ f }}
                                    text.app-text.app-mid(x="62" y="172") your files
                                    path.bq-art-wire(d="M114 100H146")
                                    path.ill-accent.ill-flow(d="M114 100H146")
                                    rect.ill-surface(x="146" y="24" width="166" height="152" rx="3")
                                    line.ill-ink(x1="146" y1="48" x2="312" y2="48")
                                    rect.app-url(x="156" y="30" width="146" height="13" rx="6.5")
                                    path.ill-accent.app-thin(d="M164.5 35.5v-1.2a2 2 0 0 1 4 0v1.2M163.5 35.5h6v4.5h-6z")
                                    text.app-text.app-small(x="174" y="40") myapp.skapi.com
                                    rect.app-soft(x="158" y="58" width="142" height="50")
                                    line.ill-accent(x1="158" y1="124" x2="232" y2="124")
                                    line.ill-line(x1="158" y1="138" x2="290" y2="138")
                                    line.ill-line(x1="158" y1="150" x2="262" y2="150")
                                    rect.app-btn(x="158" y="160" width="50" height="10")

        //- ─── why: build with your AI agents ───
        section#why.bq-section.bq-section--soft.bq-section-top
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill WHY SKAPI
                        h2 Build With Your #[span.wordset AI Agents]
                    p.bq-section-sub Let your AI agent build your backend with Skapi. Spend less time on infrastructure and more time building your application.

                //- how it goes, in four steps; while on screen the steps light up in turn
                ol.bq-flow(v-reveal="onFlowVisible")
                    li.bq-flow-step(
                        v-for="(s, i) in AGENT_FLOW"
                        :key="s.title"
                        :class="{ active: flowStep === i, done: i < flowStep }"
                    )
                        .bq-flow-art(aria-hidden="true")
                            svg(viewBox="0 0 200 110" fill="none")
                                template(v-if="i === 0")
                                    rect.ill-surface(x="14" y="12" width="172" height="86" rx="3")
                                    line.ill-ink(x1="14" y1="28" x2="186" y2="28")
                                    circle.bq-art-term-dot(v-for="x in [24, 32, 40]" :key="x" :cx="x" cy="20" r="2.2")
                                    text.app-text.app-text--ink(x="24" y="52") &gt; Build me a chat app
                                    text.app-text(x="24" y="70") for my team. My project
                                    text.app-text(x="24" y="86") ID is "abc123".#[tspan.bq-art-cursor(translate="no") &#9615;]
                                template(v-else-if="i === 1")
                                    path.ill-surface(d="M72 8h40l16 16v78H72z")
                                    path.ill-ink(d="M112 8v16h16")
                                    line.ill-line(v-for="y in [36, 46, 56]" :key="y" x1="82" :y1="y" x2="118" :y2="y")
                                    text.app-text.app-text--ink.app-mid(x="100" y="78") SKAPI.md
                                    text.app-text.app-accent.app-mid(x="100" y="93") 25 KB
                                template(v-else-if="i === 2")
                                    rect.ill-surface(x="10" y="12" width="180" height="86" rx="3")
                                    text.app-text(x="22" y="40") skapi.#[tspan.app-accent signup()]
                                    text.app-text(x="22" y="60") skapi.#[tspan.app-accent postRecord()]
                                    text.app-text(x="22" y="80") skapi.#[tspan.app-accent connectRealtime()]
                                template(v-else)
                                    rect.ill-surface(x="30" y="10" width="140" height="90" rx="3")
                                    line.ill-ink(x1="30" y1="26" x2="170" y2="26")
                                    circle.bq-art-term-dot(v-for="x in [40, 48, 56]" :key="x" :cx="x" cy="18" r="2.2")
                                    circle.app-check-ring(cx="100" cy="62" r="20")
                                    path.ill-accent(d="M91 62l6 6 12-12")
                        span.bq-step-label Step {{ i + 1 }}
                        h3 {{ s.title }}
                        p {{ s.desc }}

                //- fewer tokens: the loop a schema change costs elsewhere, and what a session reads
                .bq-tokens(v-reveal)
                    .bq-tokens-head
                        h3 Fewer Tokens to Build
                        p Every token your agent spends on infrastructure is a token not spent on your app. Skapi gives it less to do and less to read.
                    .bq-tokens-grid
                        .bq-tok-panel
                            span.bq-tok-title Every schema change
                            .bq-loop
                                //- each column's steps with the arrow that sends the agent
                                //- back to the top for the next change: a long lap, a short one
                                .bq-loop-col
                                    span.bq-loop-name Conventional backend
                                    .bq-loop-track
                                        ol.bq-loop-list
                                            li(v-for="(s, i) in LOOP_STEPS" :key="s" :style="{ '--i': i }") {{ s }}
                                        span.bq-loop-ret(aria-hidden="true")
                                    span.bq-loop-count #[strong {{ LOOP_STEPS.length }} steps] a lap, every change
                                .bq-loop-col.bq-loop-col--skapi
                                    span.bq-loop-name Skapi
                                    .bq-loop-track
                                        ol.bq-loop-list
                                            li(style="--i: 0") Edit the app
                                        span.bq-loop-ret(aria-hidden="true")
                                    span.bq-loop-count #[strong 1 step] a lap. A short loop spends fewer tokens.
                        .bq-tok-panel
                            span.bq-tok-title What your agent reads
                            .bq-pages(aria-hidden="true")
                                span.bq-page.bq-page--base SKAPI.md
                                span.bq-page(
                                    v-for="n in 32"
                                    :key="n"
                                    :class="{ 'bq-page--open': DOC_OPEN.includes(n) }"
                                    :style="{ '--k': DOC_OPEN.indexOf(n) }"
                                )
                            p.bq-tok-note The base prompt is about 25 KB: the rules, a map of the docs and an index of every method. Your agent opens a doc page only when it builds that feature, so a session carries only what it needs.

                //- any model, either way in; the two cards lead to the sections below
                .bq-any(v-reveal)
                    .bq-any-head
                        h3 Works on Any AI Platform
                        p Any coding agent, IDE or chat app, with any model. Give it the base prompt, connect the MCP server, or both.
                        ul.bq-any-logos
                            li(v-for="a in ANY_AGENTS" :key="a.key")
                                svg(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                                    path(:d="AGENT_LOGOS[a.key]")
                                span {{ a.name }}
                            li.bq-any-more + any model
                    .bq-any-routes
                        a.bq-route(href="#how" data-track="landing_route" data-track-id="prompt")
                            span.bq-step-label Base prompt
                            h4 Give it one file
                            p One markdown file teaches any model the Skapi API. Put it in your project folder, or attach it to a chat.
                            ul.bq-file-chips
                                li.bq-file-chip(v-for="p in AGENT_PLATFORMS" :key="p.filename" translate="no") {{ p.filename }}
                            span.bq-cta-link
                                | Get the base prompt
                                span.bq-arrow.bq-arrow--trail(translate="no") &darr;
                        a.bq-route(href="#mcp" data-track="landing_route" data-track-id="mcp")
                            span.bq-step-label MCP server
                            h4 Or connect the MCP server
                            p Any MCP-compatible platform, including Claude and ChatGPT. Your agent sets up and runs your project for you, signed in as you.
                            code.bq-route-url(translate="no") {{ MCP_URL }}
                            span.bq-cta-link
                                | Connect the MCP server
                                span.bq-arrow.bq-arrow--trail(translate="no") &darr;

        //- ─── performance and price ───
        section#performance.bq-section.bq-section-top
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill PERFORMANCE &amp; PRICE
                        h2 High Performance. #[span.wordset Lower Costs.]
                    p.bq-section-sub A fully serverless architecture that scales with your application while keeping operating costs low.
                .bq-perf-grid
                    .bq-perf(v-reveal)
                        //- capacity follows the traffic, bar by bar, with nobody at the controls
                        .bq-perf-art(aria-hidden="true")
                            svg(viewBox="0 0 320 180" fill="none")
                                path.ill-line(d="M30 150H306M30 20V150")
                                rect.perf-bar(v-for="(b, i) in PERF_BARS" :key="i" :x="b.x" :y="150 - b.h" width="18" :height="b.h" :style="{ '--i': i }")
                                path.perf-line(:d="PERF_TRAFFIC")
                                rect.perf-key-bar(x="34" y="164" width="10" height="10")
                                text.app-text(x="50" y="173") capacity, automatic
                                path.perf-line.perf-line--key(d="M196 169h14")
                                text.app-text(x="216" y="173") traffic
                        .bq-perf-body
                            h3 Serverless by Design, #[span.wordset Nothing to Deploy]
                            p No servers, no deployments, no capacity to plan. Your backend is live the moment you create the project, and it grows and shrinks with your traffic by itself.
                    .bq-perf(v-reveal)
                        //- a fixed server bill against paying for use: the hatched gap is idle time you pay for
                        .bq-perf-art(aria-hidden="true")
                            svg(viewBox="0 0 320 180" fill="none")
                                defs
                                    pattern#bq-hatch(width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)")
                                        line.cost-hatch(x1="0" y1="0" x2="0" y2="7")
                                rect.cost-idle(x="30" y="40" width="276" height="110" fill="url(#bq-hatch)")
                                path.cost-use(:d="COST_AREA")
                                path.ill-line(d="M30 150H306M30 20V150")
                                path.cost-fixed(d="M30 40H306")
                                text.app-text.app-text--ink(x="36" y="33") cloud servers: fixed bill
                                text.app-text.app-mid.app-halo(x="168" y="86") idle, still paid for
                                text.app-text.app-accent.app-mid.app-halo(x="168" y="173") Skapi: you pay for what you use
                        .bq-perf-body
                            h3 Cut Backend Costs #[span.wordset by Up to 90%]
                            p No servers to rent, no idle capacity, no one on call. A backend that costs hundreds a month on cloud servers can run for a tenth of that on Skapi.
                            p.bq-perf-link
                                a.bq-cta-link(href="#pricing" data-track="landing_route" data-track-id="pricing")
                                    | Start on the Free plan
                                    span.bq-arrow.bq-arrow--trail(translate="no") &darr;

        //- ─── the database Skapi runs on ───
        section#ddbd.bq-section.bq-section--soft.bq-section-top
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill DATABASE
                        h2 A Database Built #[span.wordset for AI Agents]
                    .bq-db-intro
                        //- DDBD's mark (the DDBD docs' public/logo.svg and
                        //- logo-dark.svg): ink tile, paper waves, so it inverts with the theme
                        .bq-db-brand
                            svg.bq-db-logo(viewBox="0 0 64 64" aria-hidden="true" focusable="false")
                                rect(width="64" height="64" rx="14")
                                path(d="M16 20 C21 13 27 13 32 20 S43 27 48 20")
                                path(d="M16 32 C21 25 27 25 32 32 S43 39 48 32")
                                path(d="M16 44 C21 37 27 37 32 44 S43 51 48 44")
                            span(translate="no") DDBD
                        p.bq-section-sub A schemaless record database designed for Skapi, featuring writer-based access control (WBAC).
                .bq-db-grid
                    .bq-db(v-reveal)
                        //- the writer sets the rule; the database answers each reader by it
                        .bq-db-art(aria-hidden="true")
                            svg(viewBox="0 0 320 190" fill="none")
                                circle.ill-ink(cx="34" cy="84" r="9")
                                path.ill-ink(d="M18 114a16 13 0 0 1 32 0")
                                text.app-text.app-mid(x="34" y="134") writer
                                path.bq-art-wire(d="M56 95H96")
                                path.ill-accent.ill-flow(d="M56 95H96")
                                rect.ill-surface(x="96" y="38" width="120" height="114" rx="3")
                                text.app-text.app-text--ink(x="106" y="60") { "msg": "hi" }
                                line.ill-line(x1="106" y1="74" x2="206" y2="74")
                                text.app-text(x="106" y="94") access:
                                path.ill-accent.app-thin(d="M108.5 108v-2.5a3.5 3.5 0 0 1 7 0v2.5M106.5 108h11v8h-11z")
                                text.app-text.app-accent(x="124" y="116") private
                                text.app-text(x="106" y="138") shared: bob
                                g(v-for="(r, k) in WBAC_READERS" :key="r.name")
                                    path(:class="r.ok ? 'bq-art-wire' : 'wbac-no'" :d="r.d")
                                    path.ill-accent.ill-flow(v-if="r.ok" :d="r.d")
                                    circle.app-user(cx="244" :cy="r.y" r="7")
                                    text.app-text.app-text--ink(x="257" :y="r.y + 4") {{ r.name }}
                                    path.app-pop(:class="r.ok ? 'ill-accent' : 'wbac-x'" :d="r.ok ? `M303 ${r.y}l3 3 7-7` : `M304 ${r.y - 4}l8 8M312 ${r.y - 4}l-8 8`" :style="{ '--k': k }")
                        .bq-db-body
                            span.bq-step-label Schemaless + WBAC
                            h3 Records That Carry #[span.wordset Their Own Access Rules]
                            p Save any JSON record without designing a table. With writer-based access control (WBAC), the user who writes a record decides who can read it: everyone, signed-in users, a member level, or only the writer and the people they share it with. The database checks it on every read.
                    .bq-db(v-reveal)
                        //- sealed on the device, sent and kept as ciphertext
                        .bq-db-art(aria-hidden="true")
                            svg(viewBox="0 0 320 190" fill="none")
                                rect.ill-surface(x="8" y="40" width="104" height="110" rx="3")
                                line.ill-ink(x1="8" y1="56" x2="112" y2="56")
                                text.app-text.app-text--ink.app-mid(x="60" y="86") "hello"
                                path.ill-accent(d="M60 92v10")
                                path.ill-accent(d="M52 116v-4a8 8 0 0 1 16 0v4")
                                rect.ill-block.ill-block--accent(x="48" y="116" width="24" height="18")
                                text.app-text.app-mid(x="60" y="170") the user's device
                                path.bq-art-wire(d="M112 95H222")
                                g.crypto-move
                                    rect.ill-cipher(x="122" y="88" width="14" height="14")
                                    rect.ill-cipher.ill-cipher--accent(x="144" y="88" width="14" height="14")
                                    rect.ill-cipher(x="166" y="88" width="14" height="14")
                                text.app-text.app-mid(x="166" y="78") ciphertext
                                path.ill-surface(d="M222 58v76c0 5.5 17 10 38 10s38-4.5 38-10V58")
                                ellipse.ill-surface.app-soft-fill(cx="260" cy="58" rx="38" ry="10")
                                rect.ill-cipher(v-for="(p, k) in CIPHER_STORE" :key="k" :class="{ 'ill-cipher--accent': k % 3 === 1 }" :x="p[0]" :y="p[1]" width="12" height="12")
                                text.app-text.app-mid(x="260" y="170") Skapi can't read it
                        .bq-db-body
                            span.bq-step-label Client-side encryption
                            h3 Your Data. Encrypted #[span.wordset Before It Leaves the Browser]
                            p Turn on encryption and private records are sealed with AES-256-GCM on the user's own device before they touch the network. The key comes from the user's password and never leaves the device. Not even Skapi can read them.
                //- why the two matter together, as one line of arithmetic
                .bq-db-sum(v-reveal)
                    .bq-db-term
                        Glyph.bq-icon(:shapes="ICON.braces")
                        span No schema
                    span.bq-db-op(aria-hidden="true") +
                    .bq-db-term
                        Glyph.bq-icon(:shapes="ICON.lock")
                        span Access rules in every record
                    span.bq-db-op(aria-hidden="true") =
                    .bq-db-term.bq-db-term--result
                        Glyph.bq-icon(:shapes="ICON.cloud")
                        span Nothing to migrate, nothing to deploy
                p.bq-db-note Your AI agent builds the whole backend just by writing your app's code, and the backend stays 100% serverless.
                //- DDBD's own benchmark against PostgreSQL, SQLite and MongoDB,
                //- the same chart as the DDBD docs' landing page
                DdbdBenchmark(v-reveal)
                p.bq-section-link
                    a.bq-cta-link(href="https://ddbd.skapi.com" target="_blank" rel="noopener" data-track="landing_ddbd")
                        | Learn about DDBD
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── frameworks, and where the library lives ───
        section#frameworks.bq-section.bq-section-top
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill FRAMEWORKS
                        h2 Works with #[span.wordset Any Framework]
                    p.bq-section-sub Build with plain HTML, any JavaScript framework, or Node.js. No complex setup or server deployment required.
                ul.bq-fw-grid(v-reveal)
                    li.bq-fw(v-for="(f, i) in FRAMEWORKS" :key="f.key" :style="{ '--i': i }")
                        //- a multi-colour mark is its own file, so its gradient ids stay its own
                        img(v-if="f.img" :src="f.img" alt="" width="34" height="34" loading="lazy" decoding="async")
                        svg(v-else viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                            defs(v-if="f.gradient")
                                linearGradient(:id="`bq-fw-${f.key}`" :x1="f.gradient.x1" :y1="f.gradient.y1" :x2="f.gradient.x2" :y2="f.gradient.y2")
                                    stop(v-for="s in f.gradient.stops" :key="s[0]" :offset="s[0]" :stop-color="s[1]")
                            //- what shows through the letters cut out of the mark
                            rect(v-if="f.back" v-bind="f.back")
                            path(:d="FRAMEWORK_LOGOS[f.key]" :fill="f.gradient ? `url(#bq-fw-${f.key})` : f.color")
                        span {{ f.name }}
                .bq-repo-grid(v-reveal)
                    a.bq-repo(href="https://www.npmjs.com/package/skapi-js" target="_blank" rel="noopener" data-track="landing_npm")
                        svg.bq-repo-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                            path(:d="FRAMEWORK_LOGOS.npm")
                        .bq-repo-body
                            span.bq-step-label npm
                            h3(translate="no") skapi-js
                            p The library, for any bundler or Node.js. {{ npmLatest }}
                            code(translate="no") npm i skapi-js
                        span.bq-repo-go(aria-hidden="true") &rarr;
                    a.bq-repo(href="https://github.com/broadwayinc/skapi-js" target="_blank" rel="noopener" data-track="landing_github")
                        svg.bq-repo-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                            path(:d="FRAMEWORK_LOGOS.github")
                        .bq-repo-body
                            span.bq-step-label GitHub
                            h3(translate="no") broadwayinc/skapi-js
                            p The source code of the library. Read it, follow the releases, report an issue.
                            code(translate="no") github.com/broadwayinc/skapi-js
                        span.bq-repo-go(aria-hidden="true") &rarr;

        //- ─── codebase: the same library straight from the project's code, NodeJS or a plain page ───
        section#codebase.bq-section.bq-section--soft.bq-section-top(style='padding-bottom:0')
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill HOW IT WORKS
                        //- span.bq-pill CODEBASE
                        h2 Setting Up #[span.wordset Your Codebase]
                    p.bq-section-sub A complete backend in one JavaScript library, for any JS framework or plain HTML.
                .bq-how-grid(v-reveal)
                    //- Two ways in, not steps: either one is the whole setup. Two
                    //- panels with a rule between them, no rail and no numbers.
                    .bq-ways
                        .bq-way
                            span.bq-step-label Your Skapi project
                            h3 Create a Skapi project
                            p Name it, choose a region, and you get its Project ID.
                            p.bq-step-link
                                router-link.bq-cta-link(to="/new-project" data-track="landing_step_new_project")
                                    | Create a project
                                    span.bq-arrow.bq-arrow--trail(translate="no") &rarr;
                        .bq-code-or
                            span then
                        .bq-way
                            span.bq-step-label NodeJS and SPA projects
                            h3 Install the library
                            p Install #[span(translate="no") skapi-js] from npm, create the instance once with your Project ID, and import it wherever you need it.
                            .bq-code-option
                                span.bq-code-caption bash
                                CopyText.bq-code(:value="installCmd")
                                    code #[span.tok-kw npm] i skapi-js@{{ npmVersion }}
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                            .bq-code-option
                                span.bq-code-caption javascript
                                CopyText.bq-code(:value="nodeConnectCode")
                                    //- Two lines: a br, since Vue collapses a newline in text to a space.
                                    code
                                        | #[span.tok-kw import] { #[span.tok-cls Skapi] } #[span.tok-kw from] #[span.tok-str 'skapi-js'];
                                        br
                                        | #[span.tok-kw const] skapi = #[span.tok-kw new] #[span.tok-cls Skapi](#[span.tok-str "&lt;Project ID&gt;"]);
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                        .bq-code-or
                            span or
                        .bq-way
                            span.bq-step-label Plain HTML
                            h3 Load it with a script tag
                            p No build step: load the library from the CDN and initialize it on the page. Your backend is ready.
                            .bq-code-option
                                span.bq-code-caption index.html
                                CopyText.bq-code(:value="htmlPageCode")
                                    //- One line per br, since Vue collapses a newline in text to a space; the indent is &nbsp;, which it keeps.
                                    code
                                        | #[span.tok-p &lt;!]#[span.tok-tag DOCTYPE] #[span.tok-attr html]#[span.tok-p &gt;]
                                        br
                                        | #[span.tok-p &lt;]#[span.tok-tag script] #[span.tok-attr src]=#[span.tok-str "https://cdn.jsdelivr.net/npm/skapi-js@{{ npmVersion }}/dist/skapi.js"]#[span.tok-p &gt;&lt;/]#[span.tok-tag script]#[span.tok-p &gt;]
                                        br
                                        | #[span.tok-p &lt;]#[span.tok-tag script]#[span.tok-p &gt;]
                                        br
                                        | &nbsp;&nbsp;&nbsp;&nbsp;#[span.tok-kw const] skapi = #[span.tok-kw new] #[span.tok-cls Skapi](#[span.tok-str "&lt;Project ID&gt;"]);
                                        br
                                        | #[span.tok-p &lt;/]#[span.tok-tag script]#[span.tok-p &gt;]
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                    .bq-how-art(aria-hidden="true")
                        svg.bq-art(viewBox="0 0 260 430" fill="none")
                            defs
                                radialGradient#bq-mini-grad(cx="130" cy="350" r="135" gradientUnits="userSpaceOnUse")
                                    stop(offset="0.2" stop-color="#fff")
                                    stop(offset="1" stop-color="#fff" stop-opacity="0")
                                mask#bq-mini-fade(maskUnits="userSpaceOnUse" x="0" y="0" width="260" height="430")
                                    rect(x="0" y="0" width="260" height="430" fill="url(#bq-mini-grad)")
                            g.bq-art-floor(mask="url(#bq-mini-fade)")
                                line(v-for="(l, i) in MINI_FLOOR" :key="i" :x1="l[0]" :y1="l[1]" :x2="l[2]" :y2="l[3]")
                            //- the page
                            rect.ill-surface(x="26" y="16" width="208" height="108")
                            line.ill-ink(x1="26" y1="36" x2="234" y2="36")
                            path.ill-ink(d="M37 26h.01M45 26h.01")
                            line.ill-accent(x1="44" y1="56" x2="112" y2="56")
                            line.ill-line(x1="58" y1="72" x2="186" y2="72")
                            line.ill-line(x1="58" y1="88" x2="158" y2="88")
                            line.ill-accent(x1="44" y1="104" x2="88" y2="104")
                            //- the Skapi mark between the page and the backend, on the wire
                            image.bq-art-logo(href="/skapi-logo-cut-small.png" x="107" y="160" width="46" height="57")
                            //- the backend
                            g.bq-art-slab(v-for="(s, i) in MINI_SLABS" :key="i" :class="{ 'bq-art-slab--top': s.isTop }")
                                polygon.bq-art-face-top(:points="s.top")
                                polygon.bq-art-face-left(:points="s.left")
                                polygon.bq-art-face-right(:points="s.right")
                                polygon.bq-art-led(v-for="(p, j) in s.leds" :key="j" :points="p")
                                line.bq-art-vent(v-for="(v, j) in s.vents" :key="'v' + j" :x1="v[0]" :y1="v[1]" :x2="v[2]" :y2="v[3]")
                            polygon.bq-art-pad(:points="MINI_PAD")
                            //- one wire through all three
                            path.bq-art-wire(d="M130 124V156M130 221V296")
                            path.bq-art-flow(d="M130 124V156M130 221V296")
                            rect.bq-art-port(x="127" y="121" width="6" height="6")
                            rect.bq-art-port(x="127" y="293" width="6" height="6")
                p.bq-section-link
                    a.bq-cta-link(href="https://docs.skapi.com/introduction/getting-started.html" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="codebase")
                        | Read the documentation
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── how it works: a project, the base prompt, the first prompt ───
        section#how.bq-section.bq-section--soft
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill AI CODING
                        h2 Build With Your #[span.wordset AI Agent]
                    p.bq-section-sub Code with your favorite CLI agent, IDE or #[span.wordset AI platform.]
                ol.bq-steps.bq-steps--wide(v-reveal)
                    li.bq-step
                        span.bq-step-node
                            Glyph.bq-icon(:shapes="ICON.download")
                        .bq-step-body
                            span.bq-step-label Step 1
                            h3 Download your tool's base prompt file
                            p The base prompt teaches your agent the whole Skapi API. Pick your tool below and click its file name to download it.
                            //- The same download the Settings page offers (code/agent-prompts.ts):
                            //- one file from the CDN, saved under the name the tool reads.
                            ul.bq-prompt-list
                                li.bq-prompt-row(v-for="p in AGENT_PLATFORMS" :key="p.key")
                                    svg.bq-prompt-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                                        path(:d="AGENT_LOGOS[p.key]")
                                    span.bq-prompt-tool {{ p.name }}
                                    //- the file name is the link: what the click saves
                                    a.bq-cta-link.bq-prompt-get(
                                        :href="AGENT_PROMPT_URL"
                                        :download="p.filename"
                                        data-track="landing_prompt_download"
                                        :data-track-id="p.key"
                                        :class="{ disabled: downloading }"
                                        translate="no"
                                        @click.prevent="download(p)"
                                    )
                                        | {{ downloading === p.filename ? "Saving..." : p.filename }}
                    li.bq-step
                        span.bq-step-node
                            Glyph.bq-icon(:shapes="ICON.files")
                        .bq-step-body
                            span.bq-step-label Step 2
                            h3 Put the file in your project folder
                            p Save it in your project's root folder, or attach it to your agent's prompt input.
                    li.bq-step
                        span.bq-step-node
                            Glyph.bq-icon(:shapes="ICON.terminal")
                        .bq-step-body
                            span.bq-step-label Step 3
                            h3 Open your agent and start building
                            p Paste the prompt below, replace &lt;Project ID&gt; with your Skapi Project ID, and describe what you want to build.
                            .bq-code-option
                                span.bq-code-caption prompt
                                CopyText.bq-code(:value="promptText")
                                    code
                                        | My Skapi project ID is: #[span.tok-str "&lt;Project ID&gt;"].
                                        br
                                        | Build me a #[span.tok-p [describe what you want]].
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                p.bq-section-link
                    a.bq-cta-link(href="https://docs.skapi.com/introduction/getting-started.html#building-with-your-ai-agent" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="how")
                        | Read the documentation
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── the MCP server: optional, the administrative side; three steps
        //- on the rail. The snippets and notes mirror the docs' "Connecting the
        //- Skapi MCP server" (introduction/getting-started.md); change both together ───
        section#mcp.bq-section.bq-section-top
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill MCP SERVER
                        //- span.bq-pill MCP SERVER
                        //- h2 Connecting the #[span.wordset MCP Server]
                        h2 Let Your Agent #[span.wordset Run the Project]
                    p.bq-section-sub Optional. Your agent manages your Skapi projects from your coding tool or any MCP-compatible chat platform.
                p.bq-section-intro Tickets, secret keys, OpenID login, settings, hosting, records, email templates and users: the dashboard work, done with a prompt.
                .bq-how-grid(v-reveal)
                    ol.bq-steps
                        li.bq-step
                            span.bq-step-node
                                Glyph.bq-icon(:shapes="ICON.link")
                            .bq-step-body
                                span.bq-step-label Step 1
                                h3 Connect from your tool
                                p Pick your tool and add the server with the snippet shown.
                                //- One tab per tool above phone width, a select on a
                                //- phone; both pick the same panel. Arrow keys move
                                //- between tabs.
                                .bq-tabs(role="tablist" aria-label="Coding agents")
                                    button.bq-tab(
                                        v-for="m in MCP_SETUPS"
                                        :key="m.key"
                                        type="button"
                                        role="tab"
                                        :id="`mcp-tab-${m.key}`"
                                        :aria-selected="mcpTab === m.key ? 'true' : 'false'"
                                        :aria-controls="`mcp-panel-${m.key}`"
                                        :tabindex="mcpTab === m.key ? 0 : -1"
                                        :class="{ active: mcpTab === m.key }"
                                        @click="mcpTab = m.key"
                                        @keydown.left.prevent="stepTab(-1)"
                                        @keydown.right.prevent="stepTab(1)"
                                    )
                                        svg.bq-tab-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                                            path(:d="AGENT_LOGOS[m.key]")
                                        span {{ m.name }}
                                //- A native select can hold no icons, so the box shows
                                //- the chosen tool's logo and the site's dropdown arrow
                                //- over it; the select underneath does the picking.
                                label.bq-tab-select
                                    span.bq-sr-only Coding agent
                                    svg.bq-tab-select-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                                        path(:d="AGENT_LOGOS[mcpTab]")
                                    select(v-model="mcpTab")
                                        option(v-for="m in MCP_SETUPS" :key="m.key" :value="m.key") {{ m.name }}
                                    span.material-symbols-outlined.bq-tab-select-arrow(translate="no" aria-hidden="true") arrow_drop_down
                                .bq-mcp-panel(
                                    v-for="m in MCP_SETUPS"
                                    v-show="mcpTab === m.key"
                                    :key="'panel-' + m.key"
                                    role="tabpanel"
                                    :id="`mcp-panel-${m.key}`"
                                    :aria-labelledby="`mcp-tab-${m.key}`"
                                )
                                    .bq-code-option
                                        span.bq-code-caption {{ m.lang }}
                                        CopyText.bq-code(:value="m.code")
                                            code(translate="no") {{ m.code }}
                                            span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                                    p.bq-mcp-where {{ m.where }}
                        li.bq-step
                            span.bq-step-node
                                Glyph.bq-icon(:shapes="ICON.auth")
                            .bq-step-body
                                span.bq-step-label Step 2
                                h3 Sign in with your Skapi account
                                p The first time, your browser opens the Skapi sign-in page. Standard OAuth, nothing to configure. From then on the agent acts as your account and can reach only what you can.
                        li.bq-step
                            span.bq-step-node
                                Glyph.bq-icon(:shapes="ICON.create")
                            .bq-step-body
                                span.bq-step-label Step 3
                                h3 Tell it which project
                                p An account can hold several Skapi projects. Name the one you mean, or ask the agent to list them. Then ask for what you need.
                                .bq-code-option
                                    span.bq-code-caption prompt
                                    CopyText.bq-code(:value="mcpProjectPrompt")
                                        code Use my Skapi project #[span.tok-str "&lt;Project ID&gt;"].
                                        span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                    //- The drawing: the agent's window, the MCP server signed in as the
                    //- account, the project's mark, and the dashboard forms the server
                    //- fills in, on one wire. Hidden with the grid's art column on narrow screens.
                    .bq-how-art(aria-hidden="true")
                        svg.bq-art(viewBox="0 0 260 370" fill="none")
                            //- the agent: a terminal with two prompts
                            g.bq-art-term
                                rect.bq-art-term-box.bq-art-box--light(x="26" y="14" width="208" height="74" rx="3")
                                line.bq-art-term-bar.bq-art-box--light(x1="26" y1="30" x2="234" y2="30")
                                circle.bq-art-term-dot(v-for="x in [36, 46, 56]" :key="x" :cx="x" cy="22" r="2.2")
                                text.bq-art-term-text(x="36" y="52") &gt; {{ mcpTyped[0] }}#[tspan.bq-art-cursor(v-if="mcpCursorLine === 0" translate="no") &#9615;]
                                text.bq-art-term-text(x="36" y="70" v-if="mcpCursorLine === 1") &gt; {{ mcpTyped[1] }}#[tspan.bq-art-cursor(v-if="mcpCursorLine === 1" translate="no") &#9615;]
                            //- the server, signed in as the account
                            rect.bq-art-term-box.bq-art-box--light(x="74" y="118" width="112" height="50" rx="3")
                            text.bq-art-node-text(x="130" y="141") MCP
                            text.bq-art-caption(x="130" y="158") signed in as you
                            //- the project
                            image.bq-art-logo(href="/skapi-logo-cut-small.png" x="107" y="192" width="46" height="57")
                            //- the dashboard forms it fills in
                            g.bq-art-tile(v-for="t in MCP_TILES" :key="t.label")
                                rect.bq-art-tile-shadow(:x="t.x - 19" y="293" width="44" height="44")
                                rect.bq-art-tile-box(:x="t.x - 22" y="290" width="44" height="44")
                                Glyph(as="g" :shapes="t.icon" :transform="`translate(${t.x - 12} 300)`")
                                text.bq-art-caption(:x="t.x" y="352") {{ t.label }}
                            //- one wire down through all of it, fanning out to the forms
                            path.bq-art-wire(d="M130 88V118M130 168V192M130 249V276M64 276H196M64 276V290M130 276V290M196 276V290")
                            path.bq-art-flow(d="M130 88V118M130 168V192M130 249V276")
                            rect.bq-art-port(x="127" y="85" width="6" height="6")
                p.bq-section-link
                    a.bq-cta-link(href="https://mcp.broadwayinc.computer/" target="_blank" rel="noopener" data-track="landing_mcp")
                        | Skapi MCP server
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── regions: the world, dotted, with a pin on every region a project can live in ───
        section#regions.bq-section.bq-section--soft-up
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill REGIONS
                        h2 Close to Your Users, #[span.wordset Wherever They Are]
                    p.bq-section-sub Run your backend in the region nearest your users, so every request takes the shortest hop. More regions are on the way.
                //- The land as dots (code/world-dots.ts); the pins are the regions in
                //- service (LIVE_REGIONS in the script, placed by code/regions.ts).
                //- Labels go under 640px; the list below stays.
                //- On a phone the panel runs edge to edge, borderless, and the drawing,
                //- kept at a readable scale, pans slowly and endlessly: two copies on a
                //- track, the track slid by one copy's width and looped (the Pacific
                //- meets itself at the seam). Above phone width one copy, still.
                .bq-map(v-reveal aria-hidden="true")
                    .bq-map-track
                        svg.bq-map-svg(v-for="copy in 2" :key="copy" :class="`bq-map-copy--${copy}`" :viewBox="`0 0 ${WORLD_VIEW.width} ${WORLD_VIEW.height}`" fill="none")
                            path.bq-map-dots(:d="WORLD_DOTS")
                            g.bq-map-pin(v-for="r in PINS" :key="r.region" :transform="`translate(${r.x} ${r.y})`")
                                circle.bq-map-ring(r="7")
                                circle.bq-map-spot(r="3")
                                image.bq-map-mark(href="/skapi-logo-cut-small.png" x="-9" y="-33" width="18" height="22")
                                text.bq-map-label(y="20") {{ r.city }}
                ul.bq-map-list
                    li(v-for="r in PINS" :key="r.region") {{ r.label }}

        //- ─── pricing ───
        //- The cards are the subscription page's own PlanCards, so a price,
        //- description or limit can never differ between the two. Each card is
        //- one link stretched over the whole card: a signed-in visitor gets the
        //- New Project dialog right here with that plan chosen (Free creates and
        //- opens the project, a paid plan goes to checkout), anyone else goes to
        //- the signup page. //
        section#pricing.bq-section.bq-section--grid.bq-section-top
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill PRICING
                        h2 Start Free, #[span.wordset Scale as You Grow]
                    p.bq-section-sub Every project starts on Free. Upgrade when it needs more.
                PlanCards.bq-pricing-cards(v-reveal :showFreePlan="true")
                    template(#freeAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('free')" data-track="landing_pricing_free")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Start for Free
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_free")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Start for Free
                    template(#standardAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('standard')" data-track="landing_pricing_standard")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Choose Standard
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_standard")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Choose Standard
                    template(#premiumAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('premium')" data-track="landing_pricing_premium")
                            span.bq-btn.bq-plan-cta Choose Premium
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_premium")
                            span.bq-btn.bq-plan-cta Choose Premium
                //- The cards list headline figures only; how sends are counted,
                //- what the hard stop means and the rest are in the docs.
                p.bq-section-link
                    a.bq-cta-link(:href="PLAN_DOCS.plans" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="plans")
                        | How plan limits work
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── latest articles; hidden until the list loads, for good if it fails ───
        section#news.bq-section.bq-section-top(v-if="newsArticles.length" style='padding-bottom:0')
            .bq-wrap
                .bq-section-header(v-reveal)
                    .bq-section-heading
                        span.bq-pill NEWS &amp; UPDATES
                        h2 Latest from Skapi
                    p.bq-section-sub Release notes, announcements, and stories from the team.
                .bq-card-grid(v-reveal)
                    router-link.bq-card(
                        v-for="a in newsArticles"
                        :key="a.slug"
                        :to="`/articles/${a.slug}`"
                        data-track="landing_article"
                    )
                        time.bq-news-date(translate="no") {{ a.date }}
                        h3 {{ a.title }}
                        p.bq-news-excerpt(v-if="a.excerpt") {{ a.excerpt }}
                        span.bq-card-more(aria-hidden="true") Read &rarr;
                p.bq-section-link
                    router-link.bq-cta-link(to="/articles" data-track="landing_articles_all")
                        | All articles
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── closing panel: the invitation, then the one button ───
        section.bq-section.bq-close
            .bq-wrap
                .bq-close-panel(v-reveal)
                    img(src="/skapi-logo-cut-small.png" alt="Skapi logo" width="244" height="300" loading="lazy")
                    h2 Build #[span.wordset With Us]
                    p We love people who build with passion.
                    router-link.bq-btn(
                        data-track="landing_bottom_try",
                        :to="user?.user_id ? '/my-projects' : '/signup'"
                    ) Try Skapi &rarr;

    //- From a plan card, signed in: name the project here, then Free creates and
    //- opens it and a paid plan goes to checkout (views/new-project.ts).
    CreateProject(
        :showCreateModal="showCreate"
        :initialPlan="createPlan"
        @close="showCreate = false"
    )
</template>

<script setup>
import { user } from "@/code/user";
import { npmVersion } from "@/main";
import AsciiBanner from "@/components/asciiBanner.vue";
import CopyText from "@/components/copyText.vue";
import PlanCards from "@/components/planCards.vue";
import DdbdBenchmark from "@/components/ddbdBenchmark.vue";
import CreateProject from "@/views/project-create.vue";
import { PLAN_DOCS } from "@/code/plan-copy";
import { AGENT_PLATFORMS, AGENT_PROMPT_URL, downloadAgentPrompt } from "@/code/agent-prompts";
import { AGENT_LOGOS } from "@/code/agent-logos";
import { FRAMEWORK_LOGOS } from "@/code/framework-logos";
import { catalogRegion } from "@/code/regions";
import { WORLD_DOTS, WORLD_VIEW, project } from "@/code/world-dots";
import { showToast } from "@/components/toast";
import { removeFullScreenLoader } from "@/components/jsComponents";
import { getArticles } from "@/code/articles";
import { computed, h, onBeforeUnmount, onMounted, ref } from "vue";

// A plan card, signed in: the New Project dialog with that plan chosen.
const createPlan = ref("");
const showCreate = ref(false);
const openCreate = (plan) => {
    createPlan.value = plan;
    showCreate.value = true;
};

const newsArticles = ref([]);

// Every code sample reads the one npmVersion ref from main.ts: "latest" until
// the npm registry answers, then the published version. These are the exact
// strings CopyText puts on the clipboard; the template renders the same text.
const installCmd = computed(() => `npm i skapi-js@${npmVersion.value}`);
// The tag name is spliced in so this file never contains a literal script
// tag: one would end the SFC block early, and even the escaped form trips
// editor highlighters.
const TAG = "script";
// The whole page for the plain-HTML route: the CDN tag, pinned to the version
// npmVersion holds like every other sample, then the instance.
const htmlPageCode = computed(() => `<!DOCTYPE html>
<${TAG} src="https://cdn.jsdelivr.net/npm/skapi-js@${npmVersion.value}/dist/skapi.js"></${TAG}>
<${TAG}>
    const skapi = new Skapi("<Project ID>");
</${TAG}>`);
// The module import for NodeJS and SPA projects.
const nodeConnectCode = `import { Skapi } from 'skapi-js';
const skapi = new Skapi("<Replace this with your Project ID>");`;

// ─── the headline ───
// One place to change while the candidates are compared. The parts are the
// groups the line may break between; the sub says who the agents are.
const HEADLINE = ["Instant Serverless Backend", " for Web Apps"];
const HEADLINE_SUB = "Everything your web application needs from a backend, without managing servers or infrastructure.";
// Build With Us: the support inbox, with a subject so the mail is easy to spot.

// The opening prompt, as the docs and the Settings page teach it (the same
// text code/agent-prompts.ts fills the project id into), and the one line
// that connects the MCP server (docs introduction/ai-driven.md).
const promptText = `My Skapi project ID is: "<Project ID>".
Build me a [describe what you want].`;
// The line that names the project once the MCP server is connected (docs, same section).
const mcpProjectPrompt = `Use my Skapi project "<Project ID>".`;

// ─── the MCP server ───
// The docs' per-tool snippets (introduction/getting-started.md, "Coding
// agents"), with the table's "where it goes / signing in" folded into one line
// each. Change both together. The keys are agent-logos.ts's.
const MCP_URL = "https://mcp.broadwayinc.computer";
const MCP_SETUPS = [
    {
        key: "claude",
        name: "Claude Code",
        lang: "bash",
        code: `claude mcp add --transport http skapi ${MCP_URL}`,
        where: "Add --scope user to have it in every project. Run /mcp in a session if it has not asked you to sign in.",
    },
    {
        key: "openai",
        name: "Codex CLI",
        lang: "bash",
        code: `codex mcp add skapi --url ${MCP_URL}\ncodex mcp login skapi`,
        where: "The second line signs you in.",
    },
    {
        key: "gemini",
        name: "Gemini CLI",
        lang: "bash",
        code: `gemini mcp add --transport http skapi ${MCP_URL}`,
        where: "Add -s user to have it in every project.",
    },
    {
        key: "cursor",
        name: "Cursor",
        lang: "json",
        code: `{\n  "mcpServers": {\n    "skapi": { "url": "${MCP_URL}" }\n  }\n}`,
        where: "Goes in .cursor/mcp.json in the project, or ~/.cursor/mcp.json for every project.",
    },
    {
        key: "copilot",
        name: "VS Code",
        lang: "json",
        code: `{\n  "servers": {\n    "skapi": { "type": "http", "url": "${MCP_URL}" }\n  }\n}`,
        where: "Goes in .vscode/mcp.json, or use the Command Palette: MCP: Add Server.",
    },
    {
        key: "windsurf",
        name: "Windsurf",
        lang: "json",
        code: `{\n  "mcpServers": {\n    "skapi": { "serverUrl": "${MCP_URL}" }\n  }\n}`,
        where: "Windsurf is now Devin Desktop. Goes in ~/.config/devin/mcp_config.json, opened from the MCPs entry of the Cascade panel's menu.",
    },
];

// ─── the map's pins ───
// The regions in service, the same on every environment: production's VITE_REG
// list (.env.production), so the dev site shows the real map and not its own
// two test stacks. Add a code here when a region launches. The catalogue
// (code/regions.ts) places and names each one; the label on the map is the
// city alone, the list under it the full name.
const LIVE_REGIONS = ["ap-northeast-2", "us-west-2", "ap-southeast-1", "ap-south-1", "eu-west-3", "sa-east-1"];
const PINS = LIVE_REGIONS
    .map((code) => catalogRegion(code))
    .filter((r) => r && typeof r.latitude === "number" && typeof r.longitude === "number")
    .map((r) => ({ ...r, ...project(r.latitude, r.longitude), city: r.label.split(",")[0] }));

// The chosen tab, and the arrow keys moving along the strip.
const mcpTab = ref(MCP_SETUPS[0].key);
const stepTab = (by) => {
    const i = MCP_SETUPS.findIndex((m) => m.key === mcpTab.value);
    const next = MCP_SETUPS[(i + by + MCP_SETUPS.length) % MCP_SETUPS.length];
    mcpTab.value = next.key;
    document.getElementById(`mcp-tab-${next.key}`)?.focus();
};

// The download rows, as components/agentPrompts.vue does it: the bytes come
// from the CDN and are saved under the tool's name; a dot-file loses its dot
// in the browser, so the visitor is told to put it back; a failed fetch opens
// the file as SKAPI.md instead.
const downloading = ref("");
async function download(p) {
    if (downloading.value) return;
    downloading.value = p.filename;
    try {
        const saved = await downloadAgentPrompt(p.filename);
        if (saved !== p.filename) {
            showToast(`Saved as ${saved}. Rename it to ${p.filename}: browsers drop the leading dot.`, "info");
        }
    } catch (err) {
        console.warn("[landing] could not fetch the prompt", err);
        window.open(AGENT_PROMPT_URL, "_blank", "noopener");
        showToast(`The file opened as SKAPI.md. Rename it to ${p.filename}.`, "warning");
    } finally {
        downloading.value = "";
    }
}

// ─── glyphs ───
// Feather-style 24x24 glyphs, as data, because a glyph can be drawn twice: in
// its section and on a tile of the hero drawing. A shape
// is [tag, attributes, accent]; an accent shape is stroked in the brand blue.
const ICON = {
    // a person with a check: signed in, verified
    auth: [
        ["path", { d: "M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" }],
        ["circle", { cx: 8.5, cy: 7, r: 4 }],
        ["path", { d: "M17 11l2 2 4-4" }, true],
    ],
    database: [
        ["ellipse", { cx: 12, cy: 5, rx: 9, ry: 3 }, true],
        ["path", { d: "M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" }],
        ["path", { d: "M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" }],
    ],
    // a folder with a file going up into it
    files: [
        ["path", { d: "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" }],
        ["path", { d: "M12 17v-6" }, true],
        ["path", { d: "M9 14l3-3 3 3" }, true],
    ],
    cdn: [
        ["circle", { cx: 12, cy: 12, r: 10 }],
        ["path", { d: "M2 12h20" }],
        ["path", { d: "M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" }, true],
    ],
    // a broadcast: something live, going out as it happens
    realtime: [
        ["circle", { cx: 12, cy: 12, r: 2 }, true],
        ["path", { d: "M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" }],
    ],
    email: [
        ["path", { d: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" }],
        ["path", { d: "M22 6l-10 7L2 6" }, true],
    ],
    // a browser window: the app, live on the web
    hosting: [
        ["rect", { x: 2, y: 3, width: 20, height: 18, rx: 2 }],
        ["path", { d: "M2 8h20" }, true],
        ["path", { d: "M5.5 5.5h.01M8.5 5.5h.01" }],
    ],
    // signing in through a door that is someone else's
    openid: [
        ["path", { d: "M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" }],
        ["path", { d: "M10 17l5-5-5-5" }, true],
        ["path", { d: "M15 12H3" }, true],
    ],
    key: [
        ["path", { d: "M11.39 11.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78z" }],
        ["path", { d: "M11.39 11.61L19 4M21 2l-2 2" }],
        ["path", { d: "M15.5 7.5l3 3L22 7l-3-3" }, true],
    ],
    // the webhook mark: three hooks passing a call along
    webhooks: [
        ["path", { d: "M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" }],
        ["path", { d: "M6 17l3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" }],
        ["path", { d: "M12 6l3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" }, true],
    ],
    terminal: [
        ["path", { d: "M4 17l6-6-6-6" }],
        ["path", { d: "M12 19h8" }, true],
    ],
    tokens: [
        ["circle", { cx: 8, cy: 8, r: 6 }, true],
        ["path", { d: "M18.09 10.37A6 6 0 1 1 10.34 18" }],
        ["path", { d: "M7 6h1v4" }, true],
        ["path", { d: "M16.71 13.88l.7.71-2.82 2.82" }],
    ],
    share: [
        ["circle", { cx: 18, cy: 5, r: 3 }],
        ["circle", { cx: 6, cy: 12, r: 3 }, true],
        ["circle", { cx: 18, cy: 19, r: 3 }],
        ["path", { d: "M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" }],
    ],
    create: [
        ["rect", { x: 3, y: 3, width: 18, height: 18 }],
        ["path", { d: "M12 8v8M8 12h8" }, true],
    ],
    download: [
        ["path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }],
        // shaft first, so the blue head is drawn over it
        ["path", { d: "M12 3v11" }],
        ["path", { d: "M7 10l5 5 5-5" }, true],
    ],
    link: [
        ["path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }],
        ["path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" }, true],
    ],
    // a ticket stub, torn along the dotted line
    ticket: [
        ["path", { d: "M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4z" }],
        ["path", { d: "M14 5v2M14 11v2M14 17v2" }, true],
    ],
    // a paper plane: a newsletter going out
    send: [
        ["path", { d: "M22 2L11 13" }],
        ["path", { d: "M22 2l-7 20-4-9-9-4z" }, true],
    ],
    lock: [
        ["rect", { x: 3, y: 11, width: 18, height: 11, rx: 2 }],
        ["path", { d: "M7 11V7a5 5 0 0 1 10 0v4" }, true],
    ],
    // curly braces: a JSON record, no schema
    braces: [
        ["path", { d: "M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5a2 2 0 0 0 2 2h1" }],
        ["path", { d: "M16 21h1a2 2 0 0 0 2-2v-5a2 2 0 0 1 2-2 2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" }, true],
    ],
    // a cloud with a check: live, with nothing deployed
    cloud: [
        ["path", { d: "M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" }],
        ["path", { d: "M9 14l2 2 4-4" }, true],
    ],
};

// One glyph. On its own it is an svg element; inside the hero drawing it is a
// g (as="g") and inherits the drawing's stroke. The accent is set inline: the
// shapes are made here, outside the template, so they carry no scope attribute
// for a scoped rule to match.
const Glyph = (props, { attrs }) => {
    const shapes = props.shapes.map(([tag, a, accent]) =>
        h(tag, accent ? { ...a, style: { stroke: "var(--bq-pink)" } } : a),
    );
    if (props.as === "g") return h("g", attrs, shapes);
    return h("span", { ...attrs, "aria-hidden": "true" }, [
        h("svg", {
            width: 24,
            height: 24,
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": 2,
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
        }, shapes),
    ]);
};
Glyph.props = ["shapes", "as"];

// ─── what is skapi ───
// The seven things a visitor can build, in the order of the rows, each with
// what Skapi provides for it and the drawing beside it (art: a key the
// template switches on).
const ABILITIES = [
    {
        n: 1,
        art: "login",
        title: "Add Secure Login to Your App",
        desc: "Sign-up, login, email verification and password recovery, ready to call from your page. Let people sign in with Google, Apple or any OpenID provider.",
        provides: [{ name: "Authentication", icon: ICON.auth }, { name: "OpenID Login", icon: ICON.openid }],
    },
    {
        n: 2,
        art: "files",
        title: "Let Users Upload Data and Files, and Share Them",
        desc: "Save any JSON record without designing a table. Attach files of any size, choose who can read them, and serve them fast from a global CDN.",
        provides: [{ name: "Database", icon: ICON.database }, { name: "File Storage", icon: ICON.files }, { name: "CDN", icon: ICON.cdn }],
    },
    {
        n: 3,
        art: "realtime",
        title: "Build Video Calls, Chats and Games",
        desc: "Send data between users the moment it happens: WebSocket messages for chat rooms, live updates and game moves, and WebRTC for peer-to-peer video and audio.",
        provides: [{ name: "Realtime Data", icon: ICON.realtime }],
    },
    {
        n: 4,
        art: "apikey",
        title: "Use Third-Party APIs Without Exposing Your Keys",
        desc: "Store your API keys in Skapi. Your app sends the request through Skapi, which adds the key on the way out, so the key never reaches the browser.",
        provides: [{ name: "API Secret Keys", icon: ICON.key }],
    },
    {
        n: 5,
        art: "email",
        title: "Automate Emails and Send Newsletters",
        desc: "Welcome, verification and password reset emails go out on their own, from templates you can edit. Collect subscribers and send them newsletters.",
        provides: [{ name: "Automated Emails", icon: ICON.email }, { name: "Newsletters", icon: ICON.send }],
    },
    {
        n: 6,
        art: "tickets",
        title: "Automate Processes, Bots and Payment Events",
        desc: "Register a ticket: an endpoint your payment provider, a bot or another service can call. Each call checks your conditions, then runs your actions: save a record, grant access, send an email, call another API.",
        provides: [{ name: "Tickets", icon: ICON.ticket }, { name: "Webhooks", icon: ICON.webhooks }],
    },
    {
        n: 7,
        art: "hosting",
        title: "Host Your Files and Website",
        desc: "Upload your site and it is live on its own skapi.com address, over HTTPS through a CDN. No server to set up.",
        provides: [{ name: "Web Hosting", icon: ICON.hosting }],
    },
];

// The big drawing's callouts: which row, its chip's side, the height of its
// leader, and where the leader lands on the window (ax).
const APP_CALLOUTS = [
    { n: 1, label: "Login", icon: ICON.auth, side: "l", y: 145, ax: 246 },
    { n: 2, label: "Data & Files", icon: ICON.database, side: "r", y: 392, ax: 756 },
    { n: 3, label: "Realtime", icon: ICON.realtime, side: "r", y: 166, ax: 756 },
    { n: 4, label: "API Keys", icon: ICON.key, side: "l", y: 376, ax: 246 },
    { n: 5, label: "Emails", icon: ICON.email, side: "l", y: 280, ax: 246 },
    { n: 6, label: "Automation", icon: ICON.ticket, side: "l", y: 466, ax: 246 },
    { n: 7, label: "Hosting", icon: ICON.hosting, side: "r", y: 36, ax: 600 },
];
// the file list in the drawing's window: a name and a size (or what it is)
const APP_FILES = [
    ["photo.jpg", "2.4 MB"],
    ["report.pdf", "860 KB"],
    ["{ post: 'Trip' }", "record"],
    ["video.mp4", "48 MB"],
];
// the email row's four inboxes, and the ticket row's three actions
const INBOX_Y = [34, 78, 122, 166];
const TICKET_ACTIONS = [
    { y: 40, label: "save order" },
    { y: 100, label: "grant access" },
    { y: 160, label: "send email" },
];

// ─── building with AI agents ───
const AGENT_FLOW = [
    { title: "You describe it", desc: "Tell your agent what to build, with your Project ID." },
    { title: "It reads the base prompt", desc: "One small file teaches it the whole Skapi API." },
    { title: "It writes your app", desc: "Front-end code that calls Skapi. No server code to write." },
    { title: "Your app is live", desc: "Login, data, files and the rest work from the first run." },
];
// What one schema change costs an agent on a conventional backend, step by step.
const LOOP_STEPS = [
    "List the tables",
    "Write a migration",
    "Apply it",
    "Write access policies",
    "Regenerate types",
    "Fix what breaks",
    "Edit the app",
];
// The doc pages the agent happens to open in the drawing (1-based, of 32).
const DOC_OPEN = [6, 17, 23];
// The platforms whose marks stand for "any agent".
const ANY_AGENTS = [
    { key: "claude", name: "Claude" },
    { key: "openai", name: "ChatGPT / Codex" },
    { key: "gemini", name: "Gemini" },
    { key: "copilot", name: "GitHub Copilot" },
    { key: "cursor", name: "Cursor" },
    { key: "windsurf", name: "Windsurf" },
];
// Steps light up one after another while the flow is on screen; with reduced
// motion all four stand lit.
const flowStep = ref(0);
let flowTimer = null;
const onFlowVisible = (on) => {
    clearInterval(flowTimer);
    flowTimer = null;
    if (reduceMotion) {
        flowStep.value = AGENT_FLOW.length - 1;
        return;
    }
    if (on) {
        flowTimer = setInterval(() => {
            flowStep.value = (flowStep.value + 1) % AGENT_FLOW.length;
        }, 2200);
    }
};

// ─── performance and price ───
// One made-up day of traffic, 0..1, drawn twice: as the load the capacity
// bars follow, and as the usage a pay-per-use bill follows. Illustration, not data.
const LOAD = [0.18, 0.24, 0.32, 0.58, 0.92, 0.74, 0.42, 0.3, 0.64, 0.82, 0.46, 0.26];
const PERF_BARS = LOAD.map((v, i) => ({ x: 34 + i * 23, h: Math.round(v * 112 + 8) }));
const PERF_TRAFFIC = "M" + LOAD.map((v, i) => `${43 + i * 23} ${Math.round(150 - v * 112)}`).join(" L");
const COST_AREA = "M30 150 L" + LOAD.map((v, i) => `${Math.round(30 + (i * 276) / 11)} ${Math.round(146 - v * 40)}`).join(" L") + " L306 150Z";

// ─── DDBD ───
// The readers of the WBAC drawing: who the record answers, and the path to each.
const WBAC_READERS = [
    { name: "writer", y: 50, ok: true, d: "M216 95C230 95 226 50 237 50" },
    { name: "bob", y: 95, ok: true, d: "M216 95H237" },
    { name: "others", y: 140, ok: false, d: "M216 95C230 95 226 140 237 140" },
];
// the sealed blocks stacked inside the database
const CIPHER_STORE = [[236, 80], [254, 80], [272, 80], [236, 98], [254, 98], [272, 98], [245, 116], [263, 116]];

// ─── frameworks ───
// Each mark in its brand colour (Simple Icons' hex for each). The letters of
// HTML5, JavaScript and TypeScript are cut out of the mark, so a backing shape
// (back) gives them their real colour on either theme. Next.js's black follows
// the ink, black on light and white on dark, as Next.js does itself. Angular's
// current mark is its red-to-purple gradient (angular.dev's stops). Vue,
// Vite and Solid are several colours each (Vue's green and slate, Vite's
// gradient shield and bolt, Solid's layered blues), so they are the official
// full-colour files (Devicon "original" set, MIT) in public/logos/.
const FRAMEWORKS = [
    { key: "html5", name: "HTML", color: "#E34F26", back: { x: 5, y: 4, width: 14, height: 16, fill: "#FFFFFF" } },
    { key: "javascript", name: "JavaScript", color: "#F7DF1E", back: { x: 2, y: 2, width: 20, height: 20, fill: "#000000" } },
    { key: "typescript", name: "TypeScript", color: "#3178C6", back: { x: 2, y: 2, width: 20, height: 20, fill: "#FFFFFF" } },
    { key: "react", name: "React", color: "#61DAFB" },
    { key: "vuedotjs", name: "Vue", img: "/logos/vue.svg" },
    {
        key: "angular",
        name: "Angular",
        gradient: {
            x1: 0.06, y1: 0.87, x2: 0.89, y2: 0.47,
            stops: [[0, "#E40035"], [0.24, "#F60A48"], [0.352, "#F20755"], [0.494, "#DC087D"], [0.745, "#9717E7"], [1, "#6C00F5"]],
        },
    },
    { key: "svelte", name: "Svelte", color: "#FF3E00" },
    { key: "nextdotjs", name: "Next.js" },
    { key: "vite", name: "Vite", img: "/logos/vite.svg" },
    { key: "astro", name: "Astro", color: "#BC52EE" },
    { key: "solid", name: "Solid", img: "/logos/solid.svg" },
    { key: "nodedotjs", name: "Node.js", color: "#5FA04E" },
];
// The npm card's version line, once the registry has answered.
const npmLatest = computed(() => (npmVersion.value === "latest" ? "" : `Latest: v${npmVersion.value}.`));

// ─── the hero drawing ───
// The hero's tag row, in its order. The drawing's tiles carry the same words.
const HERO_TAGS = [
    "Authentication",
    "Database",
    "File Storage",
    "Realtime Data",
    "Automated Email",
    "Web Hosting",
    "OpenID Login",
    "3rd-Party API Integrations",
];

// Four server units in 2:1 isometric projection, stacked on one axis. A unit
// is a rhombus top (half-width W, half-height H) over two side faces T tall.
const CX = 320;
const W = 116;
const H = 58;
const T = 32;
const UNIT_Y = [129, 175, 221, 267];
const pts = (list) => list.map((p) => p.join(",")).join(" ");
const unit = (cx, cy, w, h, t, isTop) => {
    // a point on a side face: s along its length, q down its height (0..1)
    const left = (s, q) => [cx - w + s * w, cy + s * h + q * t];
    const right = (s, q) => [cx + s * w, cy + h - s * h + q * t];
    return {
        isTop,
        top: pts([[cx, cy - h], [cx + w, cy], [cx, cy + h], [cx - w, cy]]),
        left: pts([left(0, 0), left(1, 0), left(1, 1), left(0, 1)]),
        right: pts([right(0, 0), right(1, 0), right(1, 1), right(0, 1)]),
        // three status lights toward the front corner of the left face
        leds: [0.56, 0.69, 0.82].map((s) =>
            pts([left(s, 0.36), left(s + 0.07, 0.36), left(s + 0.07, 0.64), left(s, 0.64)]),
        ),
        // two vent slots along the right face
        vents: [0.34, 0.66].map((q) => [...right(0.2, q), ...right(0.8, q)]),
        // where its two wires land, on the outer edge of each side face
        portL: [cx - w, cy + t / 2],
        portR: [cx + w, cy + t / 2],
    };
};
// A small rhombus on a unit's top face: where something lands on it.
const pad = (cx, cy, w) => pts([[cx, cy - w / 2], [cx + w, cy], [cx, cy + w / 2], [cx - w, cy]]);
// An isometric grid of 2n cells a side, centred on (ox, oy): the floor a
// stack stands on. A mask fades it out toward its edges.
const floor = (ox, oy, n, ax, ay) => {
    const lines = [];
    for (let i = -n; i <= n; i++) {
        lines.push([ox + (i + n) * ax, oy + (i - n) * ay, ox + (i - n) * ax, oy + (i + n) * ay]);
        lines.push([ox + (i - n) * ax, oy + (i - n) * ay, ox + (i + n) * ax, oy + (i + n) * ay]);
    }
    return lines;
};

// Bottom unit first: each one is drawn over the one below it.
const SLABS = UNIT_Y.map((cy, i) => unit(CX, cy, W, H, T, i === 0)).reverse();
const PAD = pad(CX, UNIT_Y[0], 38);
const FLOOR = floor(CX, 300, 6, 26, 13);

// The steps' drawing: two units under a web page.
const MINI_SLABS = [unit(130, 330, 84, 42, 24, false), unit(130, 296, 84, 42, 24, true)];
const MINI_PAD = pad(130, 296, 26);
const MINI_FLOOR = floor(130, 350, 4, 20, 10);

// The MCP drawing's forms: three of the dashboard pages the server fills in.
const MCP_TILES = [
    { x: 64, icon: ICON.webhooks, label: "Tickets" },
    { x: 130, icon: ICON.key, label: "Secret keys" },
    { x: 196, icon: ICON.auth, label: "Users" },
];

// What the MCP window types: the dashboard work the server does on a prompt.
// Up to 28 characters a line, the window's width at its text size.
const MCP_PROMPTS = [
    ["Save this API key as a", "secret named weather_api"],
    ["Register a ticket for the", "payment webhook"],
    ["Invite a new admin to", "my project"],
    ["Make every record in the", "posts table private"],
    ["Set up Google sign-in,", "@mycompany.com only"],
    ["Upload this page to", "my project's website"],
    ["Write a welcome e-mail and", "upload it as the template"],
    ["Show me what my orders", "ticket received today"],
    ["Turn off sign-ups until", "the launch"],
    ["List my projects and", "use the shop one"],
];

// Eight tiles, four a side, one per hero tag, each wired to its unit: out
// from the tile, a jog up or down, then in to the unit's port. The jogs are
// staggered so no two wires cross.
const TILE_Y = [46, 158, 270, 382];
const JOG = [176, 150, 150, 176];
const TILE_ICON = ["auth", "database", "files", "realtime", "email", "hosting", "openid", "key"];
const TILES = HERO_TAGS.map((tag, i) => ({
    x: i < 4 ? 76 : 564,
    y: TILE_Y[i % 4],
    icon: ICON[TILE_ICON[i]],
    // the one tag too long for a tile's width takes two lines
    label: tag === "3rd-Party API Integrations" ? ["3rd-Party API", "Integrations"] : [tag],
}));
const WIRES = TILES.map((t, i) => {
    const row = i % 4;
    const portY = UNIT_Y[row] + T / 2;
    return i < 4
        ? { d: `M104 ${t.y}H${JOG[row]}V${portY}H${CX - W}`, out: false }
        : { d: `M536 ${t.y}H${640 - JOG[row]}V${portY}H${CX + W}`, out: true };
});

// ─── the terminal's prompts ───
// What a visitor might type first. Demo filler, not site copy: two lines at
// most, 23 characters a line, so a line and its cursor fit the window at the
// drawing's 11px. Typed one character at a time, left to right, read for a
// moment, then the prompts rotate through a shuffled cycle. With reduced
// motion the first prompt simply stands there.
const PROMPTS = [
    ["Build a team task board", "with shared task lists"],
    ["Build Google sign-in", "with user profiles"],
    ["Build a blog platform", "with posts and tags"],
    ["Build a signup flow", "with welcome emails"],
    ["Build a contact form", "with email alerts"],
    ["Build a photo gallery", "with likes and comments"],
    ["Build a video chat app", "for remote teams"],
    ["Build secure storage", "for office files"],
    ["Build an order system", "for restaurant tables"],
    ["Build customer chat", "with private threads"],
    ["Build an online shop", "with secure checkout"],
    ["Build concert ticketing", "with attendee check-in"],
];
const TYPE_MS = 42;        // one character
const LINE_PAUSE_MS = 260; // before the second line starts
const READ_MS = 2600;      // the finished prompt stays this long
const reduceMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// A window that types its prompts one after another, the list in a random
// order each time round, never the same prompt twice in a row. The hero's
// window and the MCP drawing's are two of these, each with its own list.
const makeTyper = (prompts) => {
    const typed = ref(["", ""]);
    const cursorLine = ref(0);
    let timer = null;
    let queue = [];
    const next = (previous = -1) => {
        if (!queue.length) {
            queue = prompts.map((_, index) => index);
            for (let i = queue.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [queue[i], queue[j]] = [queue[j], queue[i]];
            }
            if (queue[0] === previous) {
                [queue[0], queue[1]] = [queue[1], queue[0]];
            }
        }
        return queue.shift();
    };
    const type = (index) => {
        const lines = prompts[index];
        typed.value = ["", ""];
        cursorLine.value = 0;
        let line = 0;
        let chars = 0;
        const step = () => {
            if (chars < lines[line].length) {
                chars++;
                typed.value[line] = lines[line].slice(0, chars);
                timer = setTimeout(step, TYPE_MS);
                return;
            }
            if (line + 1 < lines.length) {
                line++;
                chars = 0;
                cursorLine.value = line;
                timer = setTimeout(step, LINE_PAUSE_MS);
                return;
            }
            timer = setTimeout(() => type(next(index)), READ_MS);
        };
        step();
    };
    // with reduced motion the first prompt stands, finished, and nothing types
    const start = () => {
        if (reduceMotion) {
            typed.value = [...prompts[0]];
            cursorLine.value = prompts[0].length - 1;
        } else {
            type(next());
        }
    };
    const stop = () => clearTimeout(timer);
    return { typed, cursorLine, start, stop };
};

const heroTerm = makeTyper(PROMPTS);
const { typed, cursorLine } = heroTerm; // the hero window's lines, as its markup reads them
const mcpTerm = makeTyper(MCP_PROMPTS);
const { typed: mcpTyped, cursorLine: mcpCursorLine } = mcpTerm;

// ─── scroll reveal ───
// v-reveal: the element fades and rises in the first time it scrolls into view
// (is-in, kept), and carries is-live while on screen, which runs its looping
// drawings; off screen they pause. A function as the value is told each change.
// With reduced motion, or no IntersectionObserver, everything is simply shown.
let revealIO = null;
const revealWatchers = new Map();
const revealObserver = () => {
    if (revealIO || typeof IntersectionObserver === "undefined") return revealIO;
    revealIO = new IntersectionObserver((entries) => {
        for (const e of entries) {
            if (e.isIntersecting) e.target.classList.add("is-in");
            e.target.classList.toggle("is-live", e.isIntersecting);
            revealWatchers.get(e.target)?.(e.isIntersecting);
        }
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
    return revealIO;
};
const vReveal = {
    mounted(el, binding) {
        const watch = typeof binding.value === "function" ? binding.value : null;
        const io = reduceMotion ? null : revealObserver();
        if (!io) {
            el.classList.add("is-in", "is-live");
            watch?.(true);
            return;
        }
        if (watch) revealWatchers.set(el, watch);
        el.classList.add("bq-reveal");
        io.observe(el);
    },
    beforeUnmount(el) {
        revealIO?.unobserve(el);
        revealWatchers.delete(el);
    },
};

// The hero drawing's phone layout (see the viewBox binding): one breakpoint,
// the stylesheet's 640px, read from the browser so the two never disagree.
const phone = ref(false);
const phoneQuery = typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)") : null;
const readPhone = () => { phone.value = !!phoneQuery?.matches; };

onMounted(() => {
    readPhone();
    phoneQuery?.addEventListener("change", readPhone);
    heroTerm.start();
    mcpTerm.start();
    removeFullScreenLoader();
    getArticles()
        .then(({ articles }) => { newsArticles.value = articles.slice(0, 3); })
        .catch(() => { /* section stays hidden */ });
});

onBeforeUnmount(() => {
    phoneQuery?.removeEventListener("change", readPhone);
    heroTerm.stop();
    mcpTerm.stop();
    clearInterval(flowTimer);
    revealIO?.disconnect();
    revealIO = null;
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";
@import (reference) "@/assets/less/bq-list-page.less";
/* the sections between the hero and How It Works, and the scroll reveal */
@import "@/assets/less/landing-sections.less";

/* One step off the paper, for the shaded face of a drawn solid. */
@shade: color-mix(in srgb, @line 60%, @paper);
/* The isometric grid the hero's stack stands on, as a background: two sets of
   hairlines at the 2:1 slope. */
@iso-angle: 26.565deg;
.iso-grid(@size: 30px) {
    background-image:
        repeating-linear-gradient(@iso-angle, @line 0, @line 1px, transparent 1px, transparent @size),
        repeating-linear-gradient(-@iso-angle, @line 0, @line 1px, transparent 1px, transparent @size);
}

.bq-landing {
    min-height: 100vh;
    margin: 0;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 16px;
    line-height: 1.7;
    overflow-x: hidden;

    hr {
        background: @line;
    }
}

/* Sections are full-bleed so a band can carry its own surface; the content
   column, the navbar's width, lives in .bq-wrap. */
.bq-wrap {
    max-width: 1116px;
    margin: 0 auto;
    padding: 0 2rem;
}

/* Visually hidden, still read by screen readers and crawlers. */
.bq-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
}

/* ─── button ─── */
/* The landing's block button. Hover lift and press come from the global
   a.bq-btn rules in base.less; its colours never change on hover. */
.bq-btn {
    display: inline-block;
    padding: 0.9rem 1.75rem;
    border: 1px solid @btn-bg;
    background: @btn-bg;
    color: @paper;
    font: inherit;
    font-size: 0.92rem;
    letter-spacing: 0.01em;
    text-decoration: none;
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
    box-shadow: 2px 2px var(--bq-shadow);
}

/* ─── hero ─── */
/* The brand tint (@pink-bg), strongest under the navbar and gone by the
   hero's bottom edge, with a dot grid laid over it that fades out the same
   way. Both sit behind the content. */
.bq-hero {
    position: relative;
    isolation: isolate;
    padding: 4.5rem 0 4rem;
    background: linear-gradient(@pink-bg, transparent);

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        background-image: radial-gradient(@line 1px, transparent 1.5px);
        background-size: 24px 24px;
        background-position: center top;
        -webkit-mask-image: linear-gradient(#000 30%, transparent);
        mask-image: linear-gradient(#000 30%, transparent);
    }
}

.bq-hero-grid {
    display: grid;
    /* minmax(0, ...) so neither column can be widened by its content */
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
    gap: 2.5rem;
    align-items: center;
}

/* The banner carries its own look; the hero only places it. Deeper than the
   component's own rule so it wins wherever the stylesheets land. */
.bq-hero .bq-ascii {
    display: block;
    margin: 0;
    /* The art is 44 columns of about 0.65em, 28.4em in all, and has to fit
       the copy column: 46% of the wrap less its gutters and the grid gap. */
    font-size: min(1rem, calc((100vw - 104px) / 62));
}

.bq-hero-tagline {
    margin: 2.25rem 0 0;
    font-size: clamp(1.2rem, 2.1vw, 1.55rem);
    line-height: 1.4;
    font-weight: 600;
    text-shadow: var(--bq-text-shadow);
}

.bq-hero-sub {
    margin: 0.9rem 0 0;
    max-width: 30rem;
    color: @muted;
    font-size: 1.02rem;
}

/* Screen readers only while the drawing's labels are readable; shown as a
   row of chips at phone width (see the responsive block). */
.bq-hero-tags {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
}

.bq-hero-tag {
    padding: 0.3rem 0.7rem;
    border: 1px solid @line;
    background: @paper;
    color: @muted;
    font-size: 0.8rem;
    white-space: nowrap;
}

/* the button, and the documentation link on its own line under it */
.bq-hero-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.1rem;
    margin-top: 2.25rem;
}

.bq-hero-install {
    display: inline-block;
    max-width: 100%;
    margin-top: 2rem;
}

/* ─── the hero drawing ─── */
.bq-hero-art {
    min-width: 0;
}

.bq-art {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.bq-art-floor line {
    stroke: @line;
    stroke-width: 1;
}

.bq-art-wire {
    stroke: @line;
    stroke-width: 1.5;
}

/* Short dashes running along the wire: in toward the stack on the left, out
   from it on the right. */
.bq-art-flow {
    stroke: @pink;
    stroke-width: 2;
    stroke-dasharray: 3 21;
    animation: bq-flow 1.6s linear infinite;
}

.bq-art-flow--out {
    animation-direction: reverse;
}

@keyframes bq-flow {
    to {
        stroke-dashoffset: -24;
    }
}

.bq-art-slab polygon {
    stroke: @ink;
    stroke-width: 1.5;
}

.bq-art-face-top {
    fill: @paper;
}

.bq-art-face-left {
    fill: @hover-bg;
}

.bq-art-face-right {
    fill: @shade;
}

.bq-art-slab--top .bq-art-face-top {
    fill: @pink-bg;
}

.bq-art-slab .bq-art-led {
    fill: @pink;
    stroke: none;
    animation: bq-blink 2.4s steps(1, end) infinite;

    &:nth-of-type(5) {
        animation-delay: 0.8s;
    }

    &:nth-of-type(6) {
        fill: @muted;
        animation: none;
    }
}

/* the units do not blink in step */
.bq-art-slab:nth-of-type(odd) .bq-art-led {
    animation-duration: 3.1s;
}

@keyframes bq-blink {
    0%,
    100% {
        opacity: 1;
    }

    70% {
        opacity: 0.25;
    }
}

.bq-art-vent {
    stroke: @ink;
    stroke-width: 1.5;
    opacity: 0.45;
}

.bq-art-port {
    fill: @pink;
}

.bq-art-pad {
    stroke: @pink;
    stroke-width: 1.5;
    stroke-dasharray: 4 4;
}

.bq-art-beam {
    stroke: @pink;
    stroke-width: 1.5;
    stroke-dasharray: 2 5;
}

/* the agent's window hovers over the stack, and the mark over the wire */
.bq-art-term--bob,
.bq-art-logo {
    animation: bq-bob 4.5s ease-in-out infinite;
}

@keyframes bq-bob {
    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-6px);
    }
}

.bq-art-tile-shadow {
    fill: var(--bq-shadow);
}

.bq-art-tile-box {
    fill: @paper;
    stroke: @line;
    stroke-width: 1;
}

.bq-art-tile g {
    stroke: @ink;
    stroke-width: 2;
}

.bq-art-label {
    fill: @muted;
    font-family: @mono;
    font-size: 13.5px;
    text-anchor: middle;
}

/* the MCP drawing: the server's name in the node, and the small captions */
.bq-art-node-text {
    fill: @pink;
    font-family: @mono;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-anchor: middle;
}

.bq-art-caption {
    fill: @muted;
    font-family: @mono;
    font-size: 10px;
    text-anchor: middle;
}

/* ─── sections ─── */
.bq-section {
    padding: 6rem 0;
}

/* The hero's fade, at under half strength: a whisper of the brand tint under
   the separator, gone by the bottom, so the page ends the way it began
   without the pricing cards sitting on a visible blue band. */
.bq-section--soft {
    
    background: color-mix(in srgb, @pink-bg 40%, @paper); //linear-gradient(color-mix(in srgb, @pink-bg 40%, @paper), transparent);
}

.bq-section-top {
    border-top: 1px solid @line;
}
.bq-section-bottom {
    border-bottom: 1px solid @line;
}

/* the hero's background upside down: plain at the top, the blue tint growing
   toward the bottom (dark, then blue, in dark mode) */
.bq-section--soft-up {
    position: relative;
    isolation: isolate;
    background: linear-gradient(transparent, @pink-bg);

    /* the hero's dots, upside down too: faint at the top, full where the blue is */
    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        background-image: radial-gradient(@line 1px, transparent 1.5px);
        background-size: 24px 24px;
        background-position: center bottom;
        -webkit-mask-image: linear-gradient(transparent, #000 70%);
        mask-image: linear-gradient(transparent, #000 70%);
    }
}

/* Title on the left, the supporting line on the right, level with the
   title's last line. */
.bq-section-header {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 1rem 3.5rem;
    align-items: end;
    margin-bottom: 3.25rem;

    h2 {
        margin: 0;
        font-size: clamp(1.8rem, 3.5vw, 2.7rem);
        line-height: 1.12;
        font-weight: 700;
        letter-spacing: -0.01em;
        text-shadow: var(--bq-text-shadow);
    }
}

.bq-section-sub {
    margin: 0 0 0.3rem;
    color: @muted;
    line-height: 1.7;
}

.bq-section-link {
    margin: 2rem 0 0;
    font-size: 0.9rem;
}

/* ─── glyphs ─── */
/* Bare decorative marks. No box, border, or shadow: a framed tile read as a
   button, and these are not clickable. */
.bq-icon {
    display: inline-flex;
    flex-shrink: 0;
    color: @ink;

    :deep(svg) {
        display: block;
    }
}

/* the drawings' vocabulary */
.ill-line {
    stroke: @muted;
    stroke-width: 1;
    opacity: 0.6;
}

.ill-ink {
    stroke: @ink;
    stroke-width: 1.5;
}

.ill-surface {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

/* inside a scaled group the stroke keeps its own width */
.ill-scaled {
    vector-effect: non-scaling-stroke;
}

.ill-accent {
    stroke: @pink;
    stroke-width: 2;
}

.ill-dash {
    stroke-width: 1.5;
    stroke-dasharray: 4 4;
}

.ill-flow {
    stroke-dasharray: 3 9;
    animation: bq-flow-short 1.2s linear infinite;
}

@keyframes bq-flow-short {
    to {
        stroke-dashoffset: -12;
    }
}

.ill-block {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

.ill-block--accent {
    fill: @pink;
    stroke: @pink;
}

.ill-cipher {
    fill: @ink;
    stroke: @pink-bg;
    stroke-width: 3;
}

.ill-cipher--accent {
    fill: @pink;
}

/* ─── cards (agents, news) ─── */
.bq-card-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
}

.bq-card {
    /* the card itself is the anchor; a column so the link line sits at the
       bottom whatever the length of the copy above it */
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 2rem 1.75rem 1.75rem;
    border: 1px solid @line;
    border-top: 2px solid @pink;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    color: @ink;
    text-decoration: none;
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    &:hover {
        text-decoration: none;
    }

    /* the lift only where hover is a real pointer state */
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: translate(-1px, -1px);
            box-shadow: 4px 4px var(--bq-shadow);
        }
    }

    .bq-icon {
        margin-bottom: 1.1rem;

        :deep(svg) {
            width: 28px;
            height: 28px;
        }
    }

    h3 {
        margin: 0 0 0.6rem;
        font-size: 1.1rem;
        line-height: 1.3;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.95rem;
        line-height: 1.6;
    }
}

/* A span, since the card is already the anchor. Pinned to the card's bottom
   edge so the three link lines sit level. */
.bq-card-more {
    margin-top: auto;
    padding-top: 1.5rem;
    color: @pink;
    font-size: 0.9rem;
}

.bq-news-date {
    margin-bottom: 0.6rem;
    color: @muted;
    font-size: 0.78rem;
}

.bq-news-excerpt {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* ─── steps ─── */
/* A rail down the left, a boxed glyph at each step, the recipe beside it. */
.bq-how-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 270px;
    gap: 1rem;
    align-items: center;
}

.bq-how-art {
    min-width: 0;
}

.bq-steps {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    margin: 0;
    padding: 0;
    list-style: none;
}

.bq-step {
    position: relative;
    display: flex;
    gap: 1.75rem;
    align-items: flex-start;
    /* a grid item's min-width defaults to its content; the pre-formatted code
       would otherwise force the row wider than the container */
    min-width: 0;
    padding-bottom: 2.75rem;

    &:last-child {
        padding-bottom: 0;
    }

    /* the rail, from under this node to the next one */
    &:not(:last-child)::before {
        content: "";
        position: absolute;
        top: 3rem;
        bottom: 0;
        left: calc(1.5rem - 1px);
        border-left: 2px dotted @pink;
    }

    h3 {
        margin: 0 0 0.45rem;
        font-size: 1.15rem;
        font-weight: 600;
    }

    p {
        margin: 0;
        max-width: 76ch;
        color: @muted;
        font-size: 0.95rem;
        line-height: 1.6;
    }
}

.bq-step-node {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
}

.bq-step-body {
    flex: 1;
    min-width: 0;
}

/* "Step n" as a small brand-coloured label, in the register of the pills */
.bq-step-label {
    display: block;
    margin-bottom: 0.2rem;
    color: @pink;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

/* ─── code samples ─── */
/* The box IS CopyText's root span (fallthrough class), so a click anywhere in
   it copies. The .bq-landing prefix outranks CopyText's own scoped rules.
   The inner code element is the one that scrolls, so the corner glyph stays
   put while a long line pans. */
.bq-code-option {
    margin-top: 0.85rem;
}

.bq-code-caption {
    display: block;
    color: @muted;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.bq-landing .bq-code {
    position: relative;
    display: block;
    max-width: 100%;
    min-width: 0;
    margin-top: 0.35rem;
    padding: 0;
    border: 1px solid @input-line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    color: @ink;
    font-family: @mono;
    font-size: 0.82rem;
    line-height: 1.5;
    text-align: left;

    &:hover {
        text-decoration: none;
    }

    code {
        display: block;
        max-width: 100%;
        padding: 0.75rem 2.6rem 0.75rem 1rem;
        font: inherit;
        white-space: pre;
        overflow-x: auto;
        scrollbar-width: thin;
    }

    /* the glyph is always visible and lights up in the brand colour on hover */
    &:hover .bq-code-copy {
        color: @pink;
    }
}

.bq-code-copy {
    position: absolute;
    top: 0.6rem;
    right: 0;
    /* opaque on the box's paper, out to the box's edge, so scrolled code
       never shows through or beside it */
    padding: 0 0.6rem 0 0.4rem;
    background: @paper;
    line-height: 1;
    font-size: 1.1rem;
    color: @muted;
    transition: color 0.12s ease;
}

.bq-code-or {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0.85rem 0;
    color: @muted;
    font-size: 0.78rem;

    &::before,
    &::after {
        content: "";
        flex: 1;
        border-top: 1px solid @line;
    }
}

/* Syntax colours for the samples, drawn from the existing status palette so
   they hold up in both schemes: keywords info blue, tag and class names the
   brand blue, attribute names amber, strings green, punctuation muted. The
   markup is display only; CopyText still copies the plain string. */
.tok-kw {
    color: @info;
}

.tok-tag,
.tok-cls {
    color: @pink;
}

.tok-cls {
    font-weight: 600;
}

.tok-attr {
    color: @warning;
}

.tok-str {
    color: @success;
}

.tok-p {
    color: @muted;
}

/* ─── pricing ─── */
/* The plan cards are PlanCards' own markup. The landing restyles them into its
   card language (paper, hairline, 2px offset shadow, the same lift on hover as
   the agent and news cards) and makes each one a single link. */
.bq-plans.bq-pricing-cards {
    max-width: none;
    /* the component clips its overflow, which would cut off the hover shadow */
    overflow: visible;
    /* room for the POPULAR badge on the Premium card's top edge */
    padding-top: 0.75rem;

    :deep(.bq-plans-grid) {
        gap: 1.5rem;
    }

    :deep(.bq-plan-card) {
        /* the anchor for the stretched link below */
        position: relative;
        background: @paper;
        box-shadow: 2px 2px var(--bq-shadow);
        transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.15s;
    }

    /* the whole card is the link: the action link stretches over its card
       (the card is the positioned ancestor), and the note links in the spec
       list stay above it. A click anywhere on the card is then the link's,
       and its data-track counts it. */
    :deep(.bq-plan-action),
    :deep(.bq-plan-link) {
        position: static;
    }

    :deep(.bq-plan-link)::after {
        content: "";
        position: absolute;
        inset: 0;
    }

    :deep(.bq-plan-card a:not(.bq-plan-link)) {
        position: relative;
        z-index: 1;
    }

    @media (hover: hover) and (pointer: fine) {
        :deep(.bq-plan-card:hover) {
            transform: translate(-1px, -1px);
            box-shadow: 4px 4px var(--bq-shadow);
        }

        /* the outlined button of a hovered card turns brand blue, text and edge */
        :deep(.bq-plan-card:hover) .bq-plan-cta--ghost {
            border-color: @pink;
            color: @pink;
        }
    }

    :deep(.bq-plan-card:active) {
        transform: translate(1px, 1px);
        box-shadow: 1px 1px var(--bq-shadow);
        transition-duration: 0.04s;
    }
}

/* The card's one link: its ::after covers the whole card. The visible button
   is a span inside it, not the anchor, because the global a.bq-btn hover
   transforms the anchor and would shrink its ::after to the button's box. */
.bq-plan-link {
    display: block;
    color: inherit;
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 1;
    }

    /* the focus ring goes round the card, which is what the link stands for */
    &:focus-visible {
        outline: none;
    }

    &:focus-visible::after {
        outline: 2px solid @pink;
        outline-offset: 3px;
    }
}

.bq-plan-cta {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 0.8rem 1rem;
    text-align: center;
    white-space: nowrap;
}

/* Free and Standard: outlined, so Premium's filled button stands out. */
.bq-plan-cta--ghost {
    background: @paper;
    border-color: @input-line;
    color: @ink;
}

/* ─── closing panel ─── */
/* no rule above it: the pricing wash fades straight into this section */

.bq-close-panel {
    position: relative;
    isolation: isolate;
    padding: 4.5rem 1.5rem 5rem;
    border: 1px solid @line;
    background: @pink-bg;
    box-shadow: 4px 4px var(--bq-shadow);
    text-align: center;
    overflow: hidden;

    /* the floor grid, clear in the middle where the words are */
    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        .iso-grid(34px);
        opacity: 0.7;
        -webkit-mask-image: radial-gradient(60% 75% at 50% 50%, transparent 35%, #000);
        mask-image: radial-gradient(60% 75% at 50% 50%, transparent 35%, #000);
    }

    /* the mark above the closing line; width/height on the tag only lend the
       box its aspect ratio */
    img {
        display: block;
        width: 6.22rem;
        height: auto;
        margin: 0 auto 1.5rem;
    }

    h2 {
        margin: 0 0 0.85rem;
        font-size: clamp(1.9rem, 3.8vw, 2.8rem);
        line-height: 1.15;
        font-weight: 700;
        text-shadow: var(--bq-text-shadow);
    }

    p {
        margin: 0 0 2.25rem;
        color: @muted;
    }
}

/* ─── section backgrounds ─── */
/* Graph paper behind the reasons: a square grid of the page's rule colour,
   clearest where the cards are and fading out toward the section's edges.
   Drawn from the theme tokens, so it follows light and dark. */
.bq-section--grid {
    position: relative;
    isolation: isolate;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
    }
}

/* the rules in the brand blue, faint, so the paper reads blue rather than grey */
.bq-section--grid::before {
    background-image:
        linear-gradient(to right, @pink 1px, transparent 1px),
        linear-gradient(to bottom, @pink 1px, transparent 1px);
    background-size: 48px 48px;
    background-position: center top;
    opacity: 0.16;
    -webkit-mask-image: radial-gradient(75% 85% at 50% 50%, #000 30%, transparent);
    mask-image: radial-gradient(75% 85% at 50% 50%, #000 30%, transparent);
}

/* ─── the headline ─── */
.bq-hero-title {
    margin: 2rem 0 0;
    font-size: clamp(1.7rem, 3.1vw, 2.35rem);
    line-height: 1.15;
    font-weight: 700;
    letter-spacing: -0.01em;
    text-shadow: var(--bq-text-shadow);
}

/* ─── the agent's terminal in the drawings ─── */
.bq-art-term-box {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

.bq-art-term-bar {
    stroke: @ink;
    stroke-width: 1.5;
}

.bq-art-term-dot {
    fill: @muted;
}

/* the MCP drawing's window and node: a thin, quieter edge than the hero's */
.bq-art-box--light {
    stroke: @muted;
    stroke-width: 1;
}

.bq-art-term-text {
    font-family: @mono;
    font-size: 11px;
    fill: @ink;
}

.bq-art-cursor {
    fill: @pink;
    animation: bq-blink 1s steps(1, end) infinite;
}

/* the window's lines are a line box each; the beam and the wire pass behind them */
.bq-art-term-text {
    pointer-events: none;
}

@keyframes bq-blink {
    50% {
        opacity: 0;
    }
}

/* ─── the base prompt file names ─── */
.bq-file-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 1rem;
}

.bq-file-chip {
    padding: 0.2rem 0.55rem;
    border: 1px solid @line;
    background: @paper;
    color: @muted;
    font-family: @mono;
    font-size: 0.74rem;
    white-space: nowrap;
}

/* the base prompt rows inside their step; one column, so a tool's name is
   never cut short to fit beside another's */
.bq-prompt-list {
    margin: 1rem 0 0;
    padding: 0;
    border-bottom: 1px solid @line;
    list-style: none;
}

.bq-prompt-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
    padding: 0.6rem 0;
    border-top: 1px solid @line;
}

.bq-prompt-logo {
    flex: none;
    width: 18px;
    height: 18px;
    fill: currentColor;
}

.bq-prompt-tool {
    flex: 1;
    min-width: 0;
    font-size: 0.9rem;
}

.bq-prompt-get {
    flex: none;
    white-space: nowrap;

    &.disabled {
        pointer-events: none;
        opacity: 0.6;
    }
}

.bq-step-or {
    margin-top: 1.25rem;
}

/* a step's own link, under its text */
.bq-step .bq-step-link {
    margin-top: 0.85rem;
    font-size: 0.9rem;
}

/* a step rail on its own, with no drawing beside it: one width for every
   section that uses it, so its code boxes line up from section to section */
.bq-steps--wide {
    max-width: 58rem;
}

/* a section's longer explanation, under the header, when the sub beside the
   heading has to stay short */
.bq-section-intro {
    max-width: 58rem;
    margin: -1.5rem 0 2.5rem;
    color: @muted;
    line-height: 1.7;
}

/* ─── the map ─── */
.bq-map {
    padding: 1.5rem 1.25rem 1rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
}

.bq-map-track {
    display: flex;
    width: 100%;
}

.bq-map-svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
}

/* the second copy exists for the phone's loop only */
.bq-map-copy--2 {
    display: none;
}

@keyframes bq-pan {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(-760px);
    }
}

/* the land: zero-length strokes with round caps, one dot each */
.bq-map-dots {
    stroke: @muted;
    stroke-opacity: 0.45;
    stroke-width: 2.6;
    stroke-linecap: round;
}

.bq-map-spot {
    fill: @pink;
}

/* a ring breathing out of each pin */
.bq-map-ring {
    fill: none;
    stroke: @pink;
    stroke-width: 1.5;
    transform-box: fill-box;
    transform-origin: center;
    animation: bq-ping 2.6s ease-out infinite;
}

@keyframes bq-ping {
    0% {
        transform: scale(0.4);
        opacity: 0.9;
    }

    100% {
        transform: scale(1.6);
        opacity: 0;
    }
}

.bq-map-label {
    fill: @ink;
    font-family: @mono;
    font-size: 11px;
    font-weight: 600;
    text-anchor: middle;
    paint-order: stroke;
    stroke: @paper;
    stroke-width: 3px;
    stroke-linejoin: round;
}

/* the regions by name, under the map */
.bq-map-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    margin: 1.25rem 0 0;
    padding: 0;
    list-style: none;
    color: @muted;
    font-size: 0.9rem;

    li::before {
        content: "";
        display: inline-block;
        width: 7px;
        height: 7px;
        margin-right: 0.5rem;
        background: @pink;
        vertical-align: 1px;
    }
}

/* ─── the two ways into the codebase ─── */
.bq-ways {
    min-width: 0;
}

/* a panel per way, in the register of the cards: no node, no rail */
.bq-way {
    min-width: 0;
    padding: 1.5rem 1.6rem 1.6rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);

    h3 {
        margin: 0.35rem 0 0.45rem;
        font-size: 1.15rem;
        font-weight: 600;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.95rem;
        line-height: 1.6;
    }
}

/* the "or" rule between the two panels */
.bq-ways .bq-code-or {
    margin: 1rem 0;
}

.bq-way .bq-step-link {
    margin-top: 0.85rem;
}

/* ─── the MCP server ─── */
/* The tab strip is the dashboard's own (mail, newsletter, tickets: bq-list-page.less),
   pulled in by reference so the chosen tab looks the same everywhere: outlined pill,
   blue and a little bold when active. Only the logo and the placement are this page's. */
.bq-tabs {
    &:extend(.bq-tabs all);
    gap: 0.3rem;
    margin: 1rem 0 0;
}

/* tighter than the dashboard's (padding and letter-spacing), so all six
   tools fit the rail on one row; under 1100px the dropdown takes over */
.bq-tab {
    &:extend(.bq-tab all);
    gap: 0.4rem;
    padding: 0.4rem 0.55rem;
    letter-spacing: 0.04em;
}

/* the narrow picker: shown in place of the tabs under 1100px. The logo and
   the arrow sit over the select and let clicks through to it. */
.bq-tab-select {
    position: relative;
    display: none;
    margin: 1rem 0 0;

    select {
        width: 100%;
        padding-left: 2.7rem;
        padding-right: 2.7rem;
        background-image: none;
        box-shadow: 2px 2px var(--bq-shadow);
    }
}

.bq-tab-select-logo {
    position: absolute;
    top: 50%;
    left: 0.95rem;
    width: 18px;
    height: 18px;
    margin-top: -9px;
    fill: currentColor;
    color: @ink;
    pointer-events: none;
}

.bq-tab-select-arrow {
    position: absolute;
    top: 50%;
    right: 0.55rem;
    transform: translateY(-50%);
    color: @ink;
    font-size: 1.6rem;
    line-height: 1;
    pointer-events: none;
}

.bq-tab-logo {
    flex: none;
    width: 16px;
    height: 16px;
    fill: currentColor;
}

.bq-mcp-panel .bq-code-option {
    margin-top: 1rem;
}

/* the note under the snippet: the same breath the step copy has above the
   box. Two selectors, because .bq-step p sets every paragraph's margin to 0. */
.bq-mcp-panel .bq-mcp-where {
    margin: 1rem 0 0;
    color: @muted;
    font-size: 0.9rem;
    line-height: 1.6;
}

/* ─── motion ─── */
@media (prefers-reduced-motion: reduce) {
    .bq-art-flow,
    .bq-art-led,
    .bq-art-term--bob,
    .bq-art-logo,
    .bq-art-cursor,
    .bq-map-ring,
    .bq-map-track,
    .ill-flow {
        animation: none;
    }
}

/* ─── responsive ─── */
/* the MCP section's six tool tabs need the full rail: below 1100px the
   dropdown picks the tool instead */
@media (max-width: 1100px) {
    .bq-tabs {
        display: none;
    }

    .bq-tab-select {
        display: block;
    }
}

@media (max-width: 1000px) {
    .bq-hero-grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 3rem;
    }

    /* One column: the art has the whole row, and is sized for 47 columns
       because a motion can push it a couple of columns wide. Centered in that
       row: the component pads every row to one width, so centering each line
       keeps the drawing whole and it does not shift while a motion runs. */
    .bq-hero .bq-ascii {
        font-size: min(1rem, calc((100vw - 3rem) / 31));
    }

    /* stacked, the drawing is as wide as it needs and no wider */
    .bq-hero-art {
        width: 100%;
        max-width: 640px;
        margin: 0 auto;
    }

    .bq-section-header {
        grid-template-columns: minmax(0, 1fr);
        align-items: start;
    }

    /* no room beside the steps: they take the row */
    .bq-how-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .bq-how-art {
        display: none;
    }
}

@media (max-width: 900px) {
    .bq-wrap {
        padding: 0 1.5rem;
    }

    .bq-hero {
        padding: 2.25rem 0 3rem;
    }

    .bq-section {
        padding: 3.5rem 0;
    }

    .bq-section-header {
        margin-bottom: 2.25rem;
    }

    .bq-card-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 640px) {
    /* The drawing's labels are too small to read here, so they go. */
    .bq-art-label {
        display: none;
    }

    /* the map follows its heading straight away on a phone */
    #regions .bq-section-header {
        margin-bottom: 0;
    }

    /* The map: edge to edge and borderless, the drawing twice the screen wide
       so the pins and their labels keep a readable size, panning endlessly
       behind soft fades at both edges. */
    .bq-map {
        margin: 0 -1.5rem;
        padding: 1rem 0 0.75rem;
        border: 0;
        background: transparent;
        box-shadow: none;
        overflow: hidden;
        -webkit-mask-image: linear-gradient(to right, transparent, #000 7%, #000 93%, transparent);
        mask-image: linear-gradient(to right, transparent, #000 7%, #000 93%, transparent);
    }

    .bq-map-track {
        width: max-content;
        animation: bq-pan 24s linear infinite;
    }

    .bq-map-svg {
        width: 760px;
        min-width: 760px;
    }

    .bq-map-copy--2 {
        display: block;
    }

    /* A download row on two lines: the tool's name, then the file name link
       at the right, so a long file name never squeezes the tool's name. */
    .bq-prompt-row {
        flex-wrap: wrap;
        row-gap: 0.4rem;
    }

    .bq-prompt-tool {
        flex: 1 0 calc(100% - 18px - 0.6rem);
    }

    /* the file name link alone takes the second line, at the right */
    .bq-prompt-get {
        margin-left: auto;
        font-size: 0.9rem;
    }

    /* The terminal's lines would be too small as well, so the window is drawn
       twice as large instead, growing upward from its bottom edge into the
       headroom the viewBox gains on a phone (the binding in the template). */
    .bq-art-term-scale {
        transform-box: fill-box;
        transform-origin: 50% 100%;
        transform: scale(2);
    }

    .bq-hero-tags {
        position: static;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        width: auto;
        height: auto;
        margin: 1.5rem 0 0;
        overflow: visible;
        clip: auto;
    }

    .bq-hero-actions .bq-btn,
    .bq-close-panel .bq-btn {
        display: block;
        width: 100%;
        box-sizing: border-box;
        text-align: center;
    }

    .bq-hero-install {
        display: block;
    }

    /* a smaller node and a tighter gap: at phone width the rail was taking a
       third of the line from the step's text */
    /* no room for the rail on a phone: the node and the dotted line between
       the nodes go, and the steps keep only their labels */
    .bq-step {
        gap: 0;

        &:not(:last-child)::before {
            display: none;
        }
    }

    .bq-step-node {
        display: none;
    }

    .bq-close-panel {
        padding: 3rem 1.25rem 3.25rem;
    }
    .bq-hero .bq-ascii {
        text-align: center;
    }
}
</style>
